import { DeviceType, FilterFn } from '@renderer/types/utils'
import { Webcam } from '../../lib/webcam/webcam'
import { imageUtils } from '../imageUtils'
import { captureEncodingFor, LOSSLESS_ENCODING, resolveCaptureFormat } from '../captureEncoding'
import { Camera } from '@renderer/lib/web-gphoto2/camera'
import { useSettingsStore } from '@renderer/stores/settingsStore'
import { useCameraStore } from '@renderer/stores/cameraStore'
import { createSerialSingleFlight } from './singleFlight'
import { useBoothEventStore } from '@renderer/stores/boothEventStore'
import { isCameraRecoveryEnabled } from './cameraRecoveryFlag'
import { usableDeviceType } from './deviceTypeRepair'
import {
  forgetLiveCamera,
  getLiveCameras,
  rememberLiveCamera,
  type ReleasableCamera
} from './liveCameraRegistry'
import { installUsbUnplugWatcher } from './usbUnplug'
import {
  RENDERER_PREVIEW_SETTLE_MS,
  RENDERER_RELEASE_BUDGET_MS,
  RENDERER_RELEASE_SETTLE_MS
} from '../../../../shared/gracefulClose'
import {
  useCameraEngineBreakerStore,
  type CameraBodyIdentity
} from '@renderer/stores/cameraEngineBreakerStore'
import { useSessionStateStore } from '@renderer/stores/sessionState'
import { sendAppSettingsTelemetry } from '@renderer/utils/sendAppSettingsTelemetry'
import { useFeatureFlagsStore } from '@renderer/stores/featureFlagsStore'
import { isDigitalMode } from '@renderer/lib/runtime/mode'
import { isMobilePlatform } from '@renderer/lib/runtime/detector'
import { getDigitalFacingMode } from '@renderer/lib/digitalCamera'
import {
  parseUsbSelectionValue,
  isDualCameraEnabled,
  isNativeGphoto2Enabled,
  isReconnectV2Enabled,
  isWasmV2Enabled,
  pickWasmVariant
} from './cameraSelection'
import { NativeCamera, isNativeCameraAvailable } from '@renderer/lib/native-gphoto2/nativeCamera'
import { isFlashCaptureRetryEnabled } from './flashFlags'
import { playCaptureCue } from '../countdownSound'
import {
  FOCUS_WAIT_BUDGET_MS,
  WAIT_FOR_FOCUS_DEFAULT,
  PREFOCUS_CONFIG_NAME,
  PREFOCUS_RELEASE_CONFIG_NAME,
  decidePrefocus,
  isPrefocusReleaseKnownUnsupported,
  markPrefocusReleaseUnsupported,
  isPrefocusEnabled,
  isPrefocusKnownUnsupported,
  isPrefocusBackedOff,
  isUnsupportedConfigError,
  markPrefocusUnsupported,
  normaliseFocusMode,
  recordPrefocusOverlap,
  vendorFromModel,
  vendorFromUsbVendorId
} from './prefocus'
import {
  recordShutterAttempt,
  recordShutterCaptureDuration,
  recordShutterFailure,
  recordShutterPressDuration,
  recordShutterPressFailure,
  recordShutterSuccess
} from './captureStats'
import { initUsbSessionDeviceListener } from './usbSessionDevices'
import {
  useCameraRestStore,
  isCameraRestEnabled,
  hasPreviewConsumers
} from '@renderer/stores/cameraRestStore'
import cogoToast from '@successtar/cogo-toast'
import type { CropRect } from '../kotakCrop'
import { cameraLog, usbIdentity, type ConnectPath, type DisconnectReason } from './cameraLog'
import { describeError } from '../telemetry/boothLogClient'
import {
  connectErrorText,
  isStaleAssetError,
  isWebUsbAvailable,
  NO_WEBUSB_MESSAGE
} from './connectErrors'
import { isNewerBuildLive, scheduleReloadWhenIdle } from '../staleBuild'

interface USBCameraParams {
  productId?: number | undefined
  vendorId?: number | undefined
  /**
   * USB serial number. When present, the WebUSB request is filtered to this exact
   * physical body — the only way to pick one of two same-series cameras (which share
   * vendorId/productId). USBDeviceFilter natively supports serialNumber, so Chromium
   * narrows the chooser list before the main process ever sees it.
   */
  serialNumber?: string | undefined
}

interface ConnectResult {
  deviceInfo?: {
    productName?: string
    manufacturerName?: string
    serialNumber?: string
    vendorId?: number
    productId?: number
  }
  type: DeviceType
}

type CameraKey = 'primary' | 'secondary'

const LIVEVIEW_BUSY_RETRY_COUNT = 5
const LIVEVIEW_BUSY_RETRY_DELAY_MS = 600

/** USB productId (decimal) → min ms to wait after physical shutter before liveview fetch */
const POST_SHUTTER_LIVEVIEW_DELAY_MS_BY_PRODUCT_ID: Readonly<Record<number, number>> = {
  0x0428: 800 // Nikon D7000 (VID 04B0)
}

const lastShutterCompletedAt: Record<CameraKey, number> = { primary: 0, secondary: 0 }

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

/** Transient gphoto2 / PTP "busy" conditions — must not clear selectedCamera in capturePreview. */
function isGphotoCameraBusyError(err: unknown): boolean {
  const s = err instanceof Error ? err.message : String(err)
  return (
    /2501288/i.test(s) ||
    /2873416/i.test(s) ||
    /camera\s*busy/i.test(s) ||
    /device\s*busy/i.test(s) ||
    /ptp.*busy/i.test(s) ||
    /\b0x2019\b/i.test(s) ||
    /full-press\s*failed/i.test(s)
  )
}

/**
 * Autofocus failed on a physical-shutter capture. These are the EXACT strings libgphoto2
 * emits (verified against the strings in our libapi.wasm): the Canon EOS driver reports
 * "Canon EOS Capture failed to release: Perhaps no focus?" / "Full-Release failed" /
 * "Half-Release failed", Fuji reports "Perhaps no auto-focus?". A focus miss means the
 * CAMERA IS HEALTHY — the subject was too close/low-contrast for AF. Reconnecting (the
 * old blanket "hardware failure" recovery) is the wrong medicine; re-pressing the
 * shutter after a short settle is what an operator would do.
 */
function isGphotoFocusError(err: unknown): boolean {
  const s = err instanceof Error ? err.message : String(err)
  return /perhaps no (auto-?)?focus/i.test(s) || /\b(full|half)-release failed\b/i.test(s)
}

export type CaptureErrorKind = 'focus' | 'busy' | 'transient' | 'no-frame' | 'unknown'

export interface LastCaptureError {
  message: string
  kind: CaptureErrorKind
  at: number
}

function classifyCaptureError(err: unknown): CaptureErrorKind {
  if (isGphotoFocusError(err)) return 'focus'
  if (isGphotoCameraBusyError(err)) return 'busy'
  if (isTransientCameraReadError(err)) return 'transient'
  return 'unknown'
}

/**
 * Per-kind in-place shutter retry schedules (delay before each retry, in order) before a
 * capture attempt is declared failed.
 *
 * Two schedules, selected by the `flash_capture_retry` flag:
 *
 * LEGACY (flag OFF) is the long-standing flat policy — 2 retries per kind at a fixed
 * delay. It never outlasts a flash recycle, which is the bug, but it is the known
 * quantity every booth currently runs.
 *
 * HARDENED (flag ON) escalates busy instead of hammering it: 0x2019 covers both a dirty
 * PTP event queue (cleared by the re-drain at the retry site) and a camera refusing
 * release while an attached TTL flash recharges — speedlite recycle at booth power
 * levels runs 1–4s. Focus is unchanged; AF locks on the 2nd/3rd press or not at all.
 */
const LEGACY_SHUTTER_RETRY_DELAYS_BY_KIND: Partial<
  Record<CaptureErrorKind, readonly number[]>
> = {
  focus: [450, 450],
  busy: [600, 600],
  // A transient I/O glitch or an opaque WASM throw recovers on a re-press far more
  // cheaply than the page's hardware-recovery path (a full disconnect + reconnect, ~10s
  // of dead booth). Two short re-presses first; if they also fail, the throw still falls
  // through to recovery exactly as before — so the worst case is ~1s slower, the common
  // case skips the teardown entirely.
  transient: [500, 1000]
}

const HARDENED_SHUTTER_RETRY_DELAYS_BY_KIND: Partial<
  Record<CaptureErrorKind, readonly number[]>
> = {
  focus: [450, 450],
  busy: [500, 1000, 2000, 4000],
  transient: [500, 1000, 2000]
}

/**
 * Resolve the active schedule per capture attempt. Read at use time (not module load)
 * so a flag fetch mid-session takes effect on the next capture without a booth restart.
 */
function getShutterRetryDelays(kind: CaptureErrorKind): readonly number[] | undefined {
  const schedule = isFlashCaptureRetryEnabled()
    ? HARDENED_SHUTTER_RETRY_DELAYS_BY_KIND
    : LEGACY_SHUTTER_RETRY_DELAYS_BY_KIND
  return schedule[kind]
}

/**
 * Transient USB / PTP I/O conditions on a tethered DSLR that recover on their own.
 * These are NOT a disconnected camera — a single such error must not tear down a healthy
 * camera session (the old code nulled selectedCamera + camera on ANY non-"busy" error,
 * so one liveview hiccup stranded a paid customer mid-capture). Treated like "busy":
 * skip the frame, keep the camera, and only give up after sustained consecutive failures.
 */
function isTransientCameraReadError(err: unknown): boolean {
  const s = err instanceof Error ? err.message : String(err)
  return (
    /libusb/i.test(s) ||
    /\btimed?\s*out\b/i.test(s) ||
    /timeout/i.test(s) ||
    /could\s*not\s*claim/i.test(s) ||
    /\bI\/O\b/i.test(s) ||
    /\bio\s*error\b/i.test(s) ||
    /\bEIO\b/.test(s) ||
    /no\s*response/i.test(s) ||
    /\btransfer\b/i.test(s) ||
    /\bpipe\b/i.test(s) ||
    /\bstall\b/i.test(s) ||
    /\b0x200[0-9a-f]\b/i.test(s) || // PTP general/transient response codes (0x2000–0x200F)
    isOpaqueWasmError(s) || // a bare emscripten throw (see below)
    isDecodedWasmLibraryError(err) // the same throw, decoded (v2)
  )
}

/**
 * The WASM libgphoto2 sometimes throws an Error whose message is nothing but a large
 * integer — an emscripten exception pointer / abort code, not a camera state. The value
 * is non-deterministic run to run (2501288, 2873416 and 1506776 have all been seen), so
 * matching specific numbers is a losing game. Field data is consistent: these are a
 * transport-layer hiccup that clears on a re-press — the capture that failed with 1506776
 * on booth t949mi succeeded on the very next attempt. Treating them as transient means a
 * bounded re-press instead of a ~10s camera teardown-and-reconnect for a glitch that was
 * already gone. Anchored so only a message that is ENTIRELY a 5+ digit number matches;
 * anything with real text falls through to its own classifier.
 */
function isOpaqueWasmError(message: string): boolean {
  return /^\s*\d{5,}\s*$/.test(message)
}

/**
 * A libgphoto2 error the v2 wrapper decoded from its exception pointer (camera.js
 * decodeWasmException): a real Error with the gp message, tagged with the pointer.
 * Before decoding these were the opaque integers above and were classified transient;
 * they keep that class so retry behaviour is unchanged — only the message is new.
 */
function isDecodedWasmLibraryError(err: unknown): boolean {
  return err instanceof Error && typeof (err as { wasmPointer?: unknown }).wasmPointer === 'number'
}

/**
 * Only declare a tethered camera "gone" (null the store + clear selectedCamera) after this
 * many CONSECUTIVE preview-read failures. A genuine unplug fails every read and crosses the
 * threshold quickly; a transient blip resets on the next good frame.
 */
const MAX_CONSECUTIVE_PREVIEW_FAILURES = 4

/**
 * Thrown when a capture is attempted with no usable device for the current deviceType.
 *
 * This exists so the "no device" case is LOUD. Previously the capture dispatch was a
 * bare `if (webcam) … else if (camera) …` with no else: after a teardown the store held
 * `deviceType === 'camera' && camera === null`, neither branch matched, and the function
 * returned `undefined` before anything could throw. The caller's catch never ran, so its
 * consecutive-failure counter never advanced and the session could never escalate out of
 * the retry loop. An explicit throw restores that escalation path.
 */
/**
 * Booth baseline hook (flag `camera_baseline_settings`).
 *
 * Imported DYNAMICALLY on purpose: `boothBaseline` needs `unifiedCameraUtils`
 * to do its own reads and writes, so a static import here would close a module
 * cycle. Deferring it to call time also means the module is never even loaded
 * on booths that have the flag off. Fire-and-forget — the connect path must
 * gain no latency and no new rejection from this.
 */
function scheduleBoothBaseline(cameraKey: CameraKey): void {
  void import('./boothBaseline')
    .then((module) => module.scheduleBoothBaseline(cameraKey))
    .catch((error) => console.warn('[CAMERA-BASELINE] could not schedule:', error))
}

export class CameraUnavailableError extends Error {
  readonly deviceType: DeviceType | undefined
  readonly cameraKey: CameraKey

  constructor(cameraKey: CameraKey, deviceType: DeviceType | undefined) {
    super(
      `No usable capture device for "${cameraKey}" (deviceType=${deviceType ?? 'none'}). ` +
        `The device was disconnected or torn down.`
    )
    this.name = 'CameraUnavailableError'
    this.deviceType = deviceType
    this.cameraKey = cameraKey
  }
}

/**
 * Throw unless the store holds a device that the capture dispatch can actually use.
 * Mirrors the dispatch conditions exactly (`deviceType === X && handle`), so a state
 * this accepts is a state the dispatch can serve.
 */
function assertCaptureDeviceAvailable(cameraKey: CameraKey): void {
  const store = useCameraStore.getState()
  const isPrimary = cameraKey === 'primary'
  const deviceType = isPrimary ? store.deviceType : store.secondaryDeviceType
  const webcam = isPrimary ? store.webcam : store.secondaryWebcam
  const camera = isPrimary ? store.camera : store.secondaryCamera

  const usable = (deviceType === 'webcam' && !!webcam) || (deviceType === 'camera' && !!camera)
  if (!usable) throw new CameraUnavailableError(cameraKey, deviceType)
}

function parseUsbProductIdFromSelected(selected?: string): number | undefined {
  if (selected == null || selected === 'detect') return undefined
  const structured = parseUsbSelectionValue(selected)
  if (structured?.productId !== undefined) return structured.productId
  const n = Number(selected)
  return Number.isFinite(n) ? n : undefined
}

/**
 * Run web-gphoto2's `Camera.connect()` with `navigator.usb.getDevices` temporarily
 * scoped to ONE physical device. The vendored web-gphoto2 cannot be told which body to
 * claim ("in the future we might allow to connect to multiple cameras by passing a
 * specific instance" — upstream README); its libusb-WebUSB backend enumerates granted
 * devices and binds the first one. With two granted same-series bodies that binding is
 * undefined — this shim makes it deterministic without forking the WASM.
 */
/** Whether a (closed) WebUSB device exposes a PTP/MTP still-image interface (class 6.1). */
function isPtpUsbDevice(device: USBDevice): boolean {
  for (const config of device.configurations ?? []) {
    for (const iface of config.interfaces ?? []) {
      for (const alt of iface.alternates ?? []) {
        if (alt.interfaceClass === 6 && alt.interfaceSubclass === 1) return true
      }
    }
  }
  return false
}

/**
 * Bound a raw WebUSB call.
 *
 * `device.open()`, `reset()` and `close()` return promises that can simply never settle
 * — a device that is wedged, owned by another process, or (as seen in the field) not a
 * camera at all. Every one of those awaits used to be unbounded, so one bad device hung
 * the whole connect path and left the booth on "Connecting…" forever. Nothing here is
 * worth more than a couple of seconds: on timeout we carry on and let the next step
 * decide.
 */
async function usbCall(label: string, op: () => Promise<unknown>, ms = 1500): Promise<boolean> {
  try {
    const timedOut = Symbol('timeout')
    const result = await Promise.race([
      op().then(() => 'ok' as const),
      new Promise<typeof timedOut>((resolve) => setTimeout(() => resolve(timedOut), ms))
    ])
    if (result === timedOut) {
      console.warn(`[USB] ${label} did not answer within ${ms}ms; continuing`)
      return false
    }
    return true
  } catch (err) {
    console.warn(`[USB] ${label} failed; continuing:`, err)
    return false
  }
}

/**
 * Serialises USB device scopes. This patches a GLOBAL (`navigator.usb.getDevices`), so
 * two overlapping scopes are corrupting: the second captures the FIRST's patched
 * function as "the original", and when they unwind `getDevices` is left permanently
 * returning one hard-coded device — poisoning findGrantedUsbDevice, the disconnect
 * teardown and the breaker's presence check for the rest of the run. That is a booth
 * that binds the wrong device and never recovers until it is restarted. Running scopes
 * one at a time means `original` is always the real implementation.
 */
let usbScopeChain: Promise<unknown> = Promise.resolve()

async function withUsbDeviceScope<T>(device: USBDevice, fn: () => Promise<T>): Promise<T> {
  const enterScope = async (): Promise<T> => {
    const usbApi = navigator.usb
    const originalGetDevices = usbApi.getDevices.bind(usbApi)
    usbApi.getDevices = async (): Promise<USBDevice[]> => [device]
    try {
      return await fn()
    } finally {
      usbApi.getDevices = originalGetDevices
    }
  }
  // Flag OFF is the pre-2026-09-16 path: scopes may overlap. They patch a GLOBAL, so
  // two overlapping scopes leave getDevices permanently stubbed — which is why the ON
  // path serialises them. Kept behind the flag until that is proven on a booth.
  if (!isCameraRecoveryEnabled()) return enterScope()

  const run = usbScopeChain.then(enterScope)
  // The chain must never reject, or every later scope inherits the rejection.
  usbScopeChain = run.then(
    () => undefined,
    () => undefined
  )
  return run
}

function getPostShutterLiveviewDelayMs(
  cameraKey: CameraKey,
  settings: ReturnType<typeof useSettingsStore.getState>
): number {
  const cam =
    cameraKey === 'primary' ? settings.cameraSettings : settings.secondaryCameraSettings
  const override = cam.postShutterLiveviewDelayMs
  if (typeof override === 'number' && override >= 0) {
    return override
  }
  const selected =
    cameraKey === 'primary' ? settings.selectedCamera : settings.selectedSecondaryCamera
  const productId = parseUsbProductIdFromSelected(selected)
  if (productId !== undefined && productId in POST_SHUTTER_LIVEVIEW_DELAY_MS_BY_PRODUCT_ID) {
    return POST_SHUTTER_LIVEVIEW_DELAY_MS_BY_PRODUCT_ID[productId]
  }
  return 0
}

async function applyPostShutterLiveviewDelayIfNeeded(
  cameraKey: CameraKey,
  useShutterCapture: boolean,
  postShutterDelayMs: number
): Promise<void> {
  if (!useShutterCapture || postShutterDelayMs <= 0) return
  const last = lastShutterCompletedAt[cameraKey]
  if (last <= 0) return
  const remaining = postShutterDelayMs - (Date.now() - last)
  if (remaining > 0) {
    await sleep(remaining)
  }
}

async function captureDslrPreviewAsBlobWithPostShutterWaitAndRetry(
  camera: Camera,
  cameraKey: CameraKey
): Promise<Blob | undefined> {
  const settings = useSettingsStore.getState()
  const isPrimary = cameraKey === 'primary'
  const useShutterCapture = isPrimary
    ? settings.cameraSettings.useShutterCapture
    : settings.secondaryCameraSettings.useShutterCapture
  const postShutterDelayMs = getPostShutterLiveviewDelayMs(cameraKey, settings)
  await applyPostShutterLiveviewDelayIfNeeded(cameraKey, useShutterCapture, postShutterDelayMs)

  for (let i = 0; i < LIVEVIEW_BUSY_RETRY_COUNT; i++) {
    try {
      const blob = await camera.capturePreviewAsBlob()
      return blob ?? undefined
    } catch (err) {
      if (isGphotoCameraBusyError(err) && i < LIVEVIEW_BUSY_RETRY_COUNT - 1) {
        console.warn(
          `Liveview busy (attempt ${i + 1}/${LIVEVIEW_BUSY_RETRY_COUNT}), retrying in ${LIVEVIEW_BUSY_RETRY_DELAY_MS}ms...`
        )
        await sleep(LIVEVIEW_BUSY_RETRY_DELAY_MS)
        continue
      }
      if (isGphotoCameraBusyError(err)) {
        console.warn('Liveview still busy after retries; skipping frame', err)
        return undefined
      }
      throw err
    }
  }
  return undefined
}

function markLastShutterCompleted(cameraKey: CameraKey): void {
  lastShutterCompletedAt[cameraKey] = Date.now()
}

// Add new interface for image quality detection
interface ImageQualityResult {
  isResponding: boolean
  isImageValid: boolean
  qualityIssue?: 'dark' | 'black' | 'noise' | 'none'
}

/**
 * Backends that can fire the shutter WITHOUT the built-in autofocus half-press: the
 * native daemon (capture_no_af) and the v2 WASM build (captureImageNoAf). The v1 WASM
 * build cannot — its only capture is gp_camera_capture — which is why prefocus is
 * pointless there (see prefocus.ts).
 */
function supportsNoAfPress(camera: Camera | NativeCamera | null | undefined): boolean {
  if (!camera) return false
  if (camera instanceof NativeCamera) return true
  return camera instanceof Camera && camera.variant === 'v2'
}

/**
 * Bodies whose `eosremoterelease` widget offers none of the no-AF press choices (or that
 * lack the widget). Probing it again every shot would repeat a known failure, so after
 * the first unsupported answer the shutter goes straight to the ordinary capture.
 * Keyed per Camera instance: a reconnect gets one fresh probe.
 */
const camerasWithoutNoAfPress = new WeakSet<object>()

/**
 * The decoded message behind the most recent opaque v2 WASM exception on this camera,
 * for logging. Undefined on v1/native (no decoder) or when the last op did not crash.
 */
function wasmErrorOf(camera: Camera | NativeCamera | null | undefined): string | undefined {
  return (camera instanceof Camera ? camera.lastWasmError : null) ?? undefined
}

/** Daemon protocol this renderer needs: capture_no_af + set_single_config (daemon.c). */
const NATIVE_DAEMON_MIN_PROTOCOL = 2

/**
 * Identifies WHAT a connect is targeting, so two connects on one slot are only treated as
 * the same request when they are. A webcam is its device id; a USB body is
 * vendor:product:serial. Getting this wrong in either direction is a real bug: too coarse
 * and a camera switch silently joins the wrong attempt, too fine and a repeat tap starts
 * a second destructive connect.
 */
function connectTargetToken(deviceType: DeviceType, params: string | USBCameraParams): string {
  if (typeof params === 'string') return `${deviceType}|${params}`
  const { vendorId, productId, serialNumber } = params
  return `${deviceType}|${vendorId ?? ''}:${productId ?? ''}:${serialNumber ?? ''}`
}

/**
 * What a connect asked for, for the camera log: a webcam id is long and says nothing.
 * Never throws: it runs before the connect does.
 */
function describeConnectTarget(deviceType: DeviceType, params: string | USBCameraParams): string {
  try {
    if (typeof params === 'string') {
      return deviceType === 'webcam' ? 'webcam' : params.slice(0, 40) || 'detect'
    }
    const hex = (value?: number): string =>
      typeof value === 'number' ? value.toString(16).padStart(4, '0') : ''
    const serial = params?.serialNumber ? ` serial ${params.serialNumber}` : ''
    return `${hex(params?.vendorId)}:${hex(params?.productId)}${serial}`
  } catch {
    return 'unknown'
  }
}

class UnifiedCameraUtils {
  /** Guarantees one connect per camera slot at a time — see tryToConnect. */
  private _connectFlight = createSerialSingleFlight<CameraKey, ConnectResult>()
  private _isDisconnected: Record<CameraKey, boolean> = { primary: false, secondary: false }
  /**
   * Bumped whenever a connect STARTS on a slot. `disconnect()` takes ~1.5s of sleeps plus
   * a gphoto2 exit before its final store writes land, so a connect begun inside that
   * window used to have its `setCamera` / `setDeviceType` overwritten by the older
   * disconnect finishing afterwards — leaving `camera` set with `deviceType` still
   * 'webcam', which no branch of capturePreview matches, so the preview loop threw
   * CameraUnavailableError forever while the UI showed the camera as connected
   * (field, 2026-09-17). A disconnect now writes nothing once a newer connect owns the slot.
   */
  private _connectEpoch: Record<CameraKey, number> = { primary: 0, secondary: 0 }
  /**
   * Did this attempt fall back to the USB reset before it bound? Reported on the
   * camera_connect event as `recovered`, which is the direct measure of whether the
   * stale-session self-heal is earning its keep in the field.
   */
  private _connectUsedReset: Record<CameraKey, boolean> = { primary: false, secondary: false }
  /** Which way the current connect went (camera log only; nothing reads it to decide). */
  private _connectPath: Record<CameraKey, ConnectPath | null> = { primary: null, secondary: null }
  /**
   * The connect error as it was thrown, before the catch below rewrites it into an
   * operator message ("Harap lepas dan sambungkan kembali..."), for the camera log.
   */
  private _lastConnectRawError: Record<CameraKey, Record<string, unknown> | null> = {
    primary: null,
    secondary: null
  }
  private _configMutationDepth: Record<CameraKey, number> = { primary: 0, secondary: 0 }
  /** When each camera last ran an autofocus drive, for the prefocus throttle. */
  private _lastPrefocusAt: Record<CameraKey, number> = { primary: 0, secondary: 0 }
  /** Session each camera last focused for, for the `once-per-session` focus mode. */
  private _lastPrefocusSessionId: Record<CameraKey, string | null> = {
    primary: null,
    secondary: null
  }
  /**
   * True only while the physical shutter press is in flight. NOT `_captureDepth`: that
   * stays raised through the resize/crop/filter work after the frame is in hand, and
   * the next countdown is started from inside that window (`onRawCaptured`) — so gating
   * prefocus on it meant every shot after the first was skipped as "capture in flight".
   */
  private _shutterPressInFlight: Record<CameraKey, boolean> = { primary: false, secondary: false }
  /** True while a prefocus autofocus drive is queued or running. */
  private _prefocusDriveInFlight: Record<CameraKey, boolean> = { primary: false, secondary: false }
  /**
   * The countdown's autofocus drive, awaitable. Only consumed when the operator turned on
   * "wait for focus before capture"; otherwise it is never read and the shutter is as
   * prompt as it has always been.
   */
  private _prefocusDrivePromise: Record<CameraKey, Promise<void> | null> = {
    primary: null,
    secondary: null
  }
  /**
   * A drive has run since the last shutter press, so the body is sitting in the
   * autofocus-engaged state and must be released before the press (see
   * PREFOCUS_RELEASE_CONFIG_NAME). Never set on booths that did not drive, so an
   * unflagged or Fixed-focus booth never pays the release.
   */
  private _prefocusEngaged: Record<CameraKey, boolean> = { primary: false, secondary: false }
  /** Consecutive capturePreview failures per camera; reset on any successful read. */
  private _consecutivePreviewFailures: Record<CameraKey, number> = { primary: 0, secondary: 0 }
  /**
   * Depth of in-flight captureImage/captureImageAsFile calls per camera. While > 0 the
   * device is actively shooting; the View health monitor must not issue a competing liveview
   * read (USB/PTP is single-access — contention is what *causes* the transient "busy"/I/O
   * errors) nor tear the camera down mid-shot. Counter (not boolean) to survive nesting
   * (captureImage -> captureImageAsFile).
   */
  private _captureDepth: Record<CameraKey, number> = { primary: 0, secondary: 0 }
  /**
   * The WebUSB device each cameraKey claimed at connect time. Enables (a) scoped
   * teardown — disconnecting one camera must not close its twin's device (the old code
   * iterated ALL of navigator.usb.getDevices()), and (b) deterministic web-gphoto2
   * binding via withUsbDeviceScope.
   */
  private _claimedUsbDevice: Record<CameraKey, USBDevice | null> = {
    primary: null,
    secondary: null
  }
  /**
   * Last classified capture failure per camera key. Fleet logs showed EVERY capture
   * failure reaching Mongo as the generic "No image data captured." because captureImage
   * swallowed the real error at this boundary — focus misses, PTP busy, and genuine USB
   * death were indistinguishable in the field. Pages read this to log the truth and to
   * pick the right remedy (retry-in-place for focus vs full reconnect for hardware).
   */
  private _lastCaptureError: Record<CameraKey, LastCaptureError | null> = {
    primary: null,
    secondary: null
  }
  /** Timestamp of the last successful liveview frame per key (any reader). */
  private _lastGoodPreviewAt: Record<CameraKey, number> = { primary: 0, secondary: 0 }
  /**
   * Camera keys with a page-level recovery in flight. While set, the View health
   * monitor must not run a competing teardown/prompt and the idle-eject timer must not
   * disconnect the camera mid-reconnect — two of the "authorities fighting" races that
   * turned single hiccups into ~29-minute fleet incidents.
   */
  private _recoveringKeys = new Set<CameraKey>()

  /**
   * The classified error from the capture attempt that JUST finished — bounded by
   * maxAgeMs so a page can never act on a stale classification. Without the bound, a
   * focus miss recorded minutes ago could later mask a genuine hardware failure whose
   * own error went unrecorded, and the page would skip a recovery it actually needed.
   */
  getLastCaptureError(
    cameraKey: CameraKey = 'primary',
    maxAgeMs: number = 15000
  ): LastCaptureError | null {
    const lastError = this._lastCaptureError[cameraKey]
    if (!lastError || Date.now() - lastError.at > maxAgeMs) return null
    return lastError
  }

  private recordCaptureError(cameraKey: CameraKey, err: unknown, kindOverride?: CaptureErrorKind): void {
    const message = err instanceof Error ? err.message : String(err)
    this._lastCaptureError[cameraKey] = {
      message,
      kind: kindOverride ?? classifyCaptureError(err),
      at: Date.now()
    }
  }

  setRecovering(cameraKey: CameraKey, active: boolean): void {
    if (active) this._recoveringKeys.add(cameraKey)
    else this._recoveringKeys.delete(cameraKey)
  }

  /** Connecting or self-healing — see the unplug watcher. */
  isReconnecting(cameraKey: CameraKey): boolean {
    return this._connectFlight.isInFlight(cameraKey) || this._recoveringKeys.has(cameraKey)
  }

  /** Gate for the unplug watcher — OFF restores discover-on-failure exactly. */
  isUnplugWatchEnabled(): boolean {
    return isCameraRecoveryEnabled()
  }

  isAnyRecoveryActive(): boolean {
    return this._recoveringKeys.size > 0
  }

  /**
   * Camera Rest (flag `camera_rest`): PARK the camera — stop liveview while KEEPING the
   * USB claim, the WebUSB grant, and the WASM context alive. Best-effort `viewfinder=0`
   * closes the mirror (the audible "unclick" of a resting booth camera); bodies without
   * the widget still get "liveview reads stop", which is most of the wear win. Unlike
   * the legacy idle-eject (full disconnect), waking a parked camera never needs
   * `navigator.usb.requestDevice()` — so no user gesture and no "Click to Start Camera"
   * prompt. See docs/camera-rest-plan.md.
   */
  async parkCamera(cameraKey: CameraKey = 'primary'): Promise<boolean> {
    if (!isCameraRestEnabled()) return false
    const rest = useCameraRestStore.getState()
    if (rest.isParked(cameraKey)) return true
    if (this._isDisconnected[cameraKey]) return false
    // Never park under an in-flight capture or recovery — the same "authorities
    // fighting" guard the idle-eject learned the hard way (fleet incident data).
    if (this._captureDepth[cameraKey] > 0 || this._recoveringKeys.has(cameraKey)) {
      console.log(`[CAMERA-REST] Refusing to park ${cameraKey}: capture/recovery in flight`)
      return false
    }
    // Never park a camera whose liveview is ON SCREEN. Parking suspends the pull
    // loop, so a mounted Preview would freeze on its last frame in front of the
    // guest. This is the single authoritative guard: single-page booths
    // (bahela-receipt / bahela-quick, all newspaper Welcome screens) show a live
    // preview on the SAME route the Welcome idle timer watches, and a guest
    // posing or waiting on a QRIS scan generates no DOM input events for 60s.
    if (hasPreviewConsumers(cameraKey)) {
      console.log(`[CAMERA-REST] Refusing to park ${cameraKey}: live preview on screen`)
      return false
    }
    const store = useCameraStore.getState()
    const isPrimary = cameraKey === 'primary'
    const deviceType = isPrimary ? store.deviceType : store.secondaryDeviceType
    const camera = isPrimary ? store.camera : store.secondaryCamera
    // DSLR-only: webcams have no shutter/mirror wear and make no click.
    if (deviceType !== 'camera' || !camera) return false

    rest.setParked(cameraKey, true)
    // Let Preview loops observe the flag and any in-flight liveview read drain before
    // the config write — a viewfinder write racing a preview read manufactures exactly
    // the transient busy/IO errors this feature exists to avoid.
    await sleep(300)
    try {
      await this.setConfigValue(cameraKey, 'viewfinder', 0)
      console.log(`[CAMERA-REST] Parked ${cameraKey} (viewfinder=0)`)
    } catch (err) {
      // Widget missing/rejected on this model (e.g. Sony): parked = reads stopped only.
      console.warn(`[CAMERA-REST] Parked ${cameraKey} without viewfinder toggle:`, err)
    }
    return true
  }

  /**
   * Wake a parked camera. One liveview read restarts liveview (raises the mirror on
   * Canon/Nikon — no explicit `viewfinder=1` needed) and doubles as health
   * verification for a camera that died while parked. Returns false when the camera
   * is gone — callers fall back to the legacy disconnect + CameraPrompt path, so the
   * worst case equals today's behavior. No-op (true) when not parked.
   */
  async wakeCamera(cameraKey: CameraKey = 'primary'): Promise<boolean> {
    const rest = useCameraRestStore.getState()
    if (!rest.isParked(cameraKey)) return true
    rest.setParked(cameraKey, false)
    const store = useCameraStore.getState()
    const isPrimary = cameraKey === 'primary'
    const camera = isPrimary ? store.camera : store.secondaryCamera
    if (!camera) return false
    try {
      const blob = await captureDslrPreviewAsBlobWithPostShutterWaitAndRetry(camera, cameraKey)
      if (blob) {
        this._consecutivePreviewFailures[cameraKey] = 0
        this._lastGoodPreviewAt[cameraKey] = Date.now()
        console.log(`[CAMERA-REST] Woke ${cameraKey}`)
        return true
      }
      console.warn(`[CAMERA-REST] Wake read returned no frame for ${cameraKey}`)
      return false
    } catch (err) {
      console.error(`[CAMERA-REST] Wake failed for ${cameraKey}:`, err)
      return false
    }
  }

  /**
   * Find a still-granted WebUSB device matching a stored selection (Reconnect v2).
   * Serial-qualified selections match exactly; legacy bare-productId matches vid/pid
   * (no PTP check — the id came from a camera enumeration the operator picked);
   * 'detect' (no params) accepts any PTP still-image device. Never returns the body
   * the OTHER camera key currently claims.
   */
  private findGrantedUsbDevice(
    devices: USBDevice[],
    usbParams: USBCameraParams | undefined,
    cameraKey: CameraKey,
    /**
     * Product id from a LEGACY bare-productId selection (e.g. "12927"), which
     * parseUsbSelectionValue cannot read because it carries no `usb:` prefix. Most of
     * the fleet stores its camera that way, so without this the reconnect had nothing
     * to match on and fell through to "first PTP device".
     */
    fallbackProductId?: number
  ): USBDevice | undefined {
    const otherKey: CameraKey = cameraKey === 'primary' ? 'secondary' : 'primary'
    const otherClaimed = this._claimedUsbDevice[otherKey]
    // A body we bind must be a camera. Everything below picks from PTP devices only:
    // the granted list also holds whatever else this booth ever authorised, and a
    // gesture-free reconnect that grabs one of those is worse than no reconnect at all
    // — in the field it bound an internal webcam, then hung trying to reset it.
    const candidates = devices.filter((d) => d !== otherClaimed && isPtpUsbDevice(d))
    if (usbParams?.serialNumber) {
      return candidates.find(
        (d) =>
          d.serialNumber === usbParams.serialNumber &&
          (!usbParams.vendorId || d.vendorId === usbParams.vendorId) &&
          (!usbParams.productId || d.productId === usbParams.productId)
      )
    }
    const productId = usbParams?.productId ?? fallbackProductId
    if (productId !== undefined) {
      const exact = candidates.find(
        (d) => d.productId === productId && (!usbParams?.vendorId || d.vendorId === usbParams.vendorId)
      )
      // The remembered body is gone (swapped/unplugged): fall back to any camera rather
      // than refusing to reconnect, but never to a non-camera.
      if (exact) return exact
    }
    return candidates.find(isPtpUsbDevice)
  }

  /**
   * One liveview read to prove a (re)connected DSLR is actually streaming — not merely that
   * `connect()` resolved. A "busy"/contention PTP error means the body is alive but momentarily
   * occupied (still healthy); any other error or a null blob means the session is stale.
   */
  private async probeLiveviewAlive(camera: Camera): Promise<boolean> {
    try {
      const blob = await camera.capturePreviewAsBlob()
      return !!blob
    } catch (err) {
      return isGphotoCameraBusyError(err)
    }
  }

  /**
   * Reset a WebUSB device at the USB layer to clear a stale PTP session in software — the
   * equivalent of unplugging and replugging the cable. Used before re-binding a granted DSLR
   * whose soft reconnect bound but won't stream. Best-effort: some platforms/devices throw on
   * these calls, and a failure here must never block the reconnect that follows. Leaves the
   * device closed so libgphoto2 can open + claim it fresh on the next connect().
   */
  private async resetUsbDeviceBestEffort(device: USBDevice): Promise<void> {
    // Every step bounded: this runs on the connect path, and a device that never answers
    // must cost a second, not the session. (Field 2026-09-16: an unbounded reset on a
    // device that was not even the camera left the booth stuck on "Connecting…".)
    if (!device.opened) {
      await usbCall('open', () => device.open())
    }
    // Interface 0 is the PTP/MTP interface libgphoto2 claims; release it so neither the
    // reset nor the re-claim is blocked by a lingering claim from the dead session.
    await usbCall('releaseInterface(0)', () => device.releaseInterface(0), 800)
    const reset = await usbCall('reset', () => device.reset(), 2000)
    if (reset) console.log('[uniCamUtils] USB device reset to clear stale PTP session')
    if (device.opened) await usbCall('close', () => device.close(), 800)
  }

  /**
   * Connect web-gphoto2 to a body, self-healing a session left open by a previous
   * process. When the app is closed (or crashes, or loses power) WITHOUT running the
   * libgphoto2 goodbye — `gp_camera_exit`, which sends the Canon SetRemoteMode(1) +
   * CloseSession — the OS drops the USB pipe but the camera never hears it, and stays
   * in remote mode with an open PTP session. The next launch's `gp_camera_init` then
   * fails against that stale session, which the field sees as "please replug the USB
   * cable". A WebUSB `device.reset()` is the software equivalent of replugging: it
   * bus-resets the body and clears the session. So: try connect; on failure, tear down
   * the half-open attempt, USB-reset, and retry ONCE. The retry's failure bubbles to
   * the caller's existing handler (which still surfaces the replug message as the true
   * last resort). This is what makes a relaunch reconnect with no cable touch.
   *
   * @param connect binds this camera — `withUsbDeviceScope(device, …)` on the dual
   *   path, a bare call on the single path; reset runs OUTSIDE the scope, directly on
   *   the WebUSB device, so it needs no libgphoto2 binding.
   */
  private async connectClearingStaleSession(
    cameraKey: CameraKey,
    camera: Camera,
    usbDevice: USBDevice,
    connect: () => Promise<void>
  ): Promise<void> {
    try {
      await connect()
      return
    } catch (firstErr) {
      this.logCameraActivity('Camera connect failed; USB-resetting to clear a stale session', {
        cameraKey,
        error: firstErr instanceof Error ? firstErr.message : String(firstErr)
      })
      cameraLog.resetRetry(cameraKey, firstErr)
      // Free the failed attempt's libusb/WASM state first, so the reset owns the device
      // and the retry rebuilds cleanly. discardModule() is what makes the retry
      // meaningful: reusing an instance whose libusb still believes it owns the device
      // just hangs again, which is how a booth ended up needing a physical unplug.
      await camera.disconnect().catch(() => undefined)
      camera.discardModule()
      this._connectUsedReset[cameraKey] = true
      await this.resetUsbDeviceBestEffort(usbDevice)
      await connect()
      this.logCameraActivity('Camera reconnected after USB reset (no cable touch needed)', {
        cameraKey
      })
    }
  }

  async getDevices(): Promise<{ webcams: MediaDeviceInfo[]; pairedCameras: USBDevice[] }> {
    try {
      const webcams = await Webcam.getDevices()
      // No WebUSB: still return the webcams instead of dropping them in the catch.
      const pairedCameras = isWebUsbAvailable() ? await navigator.usb.getDevices() : []
      return { webcams, pairedCameras }
    } catch (err) {
      console.error('Error getting devices:', err)
      return { webcams: [], pairedCameras: [] }
    }
  }

  /**
   * Connect a camera, then hand a successful DSLR connect to the booth baseline.
   *
   * The scheduling lives HERE, wrapping the whole thing, rather than next to the
   * returns inside it: `tryToConnectInner` has five exit points and the two that
   * matter most in the field — the Reconnect-v2 soft path and the legacy
   * requestDevice path — both return an object carrying `deviceInfo`. Scheduling
   * at individual returns missed exactly those two, so on a real booth the
   * baseline never ran at all. One wrapper cannot be missed by a future exit.
   *
   * Fire-and-forget and internally delayed, so the connect path (the flakiest
   * window in the app) gains no latency and no new rejection. Scheduling twice
   * is harmless — the baseline no-ops per camera instance.
   */
  /**
   * One connect per camera slot at a time. A repeated tap on "Connecting…" JOINS the
   * attempt already running instead of starting a second one — see singleFlight.ts for
   * why a second attempt is not merely wasteful but destructive (concurrent
   * gp_camera_init in one WASM instance, each able to evict the module the other is
   * using). This is the guarantee that a booth cannot be hammered into a hang.
   */
  async tryToConnect(
    deviceType: DeviceType,
    params: string | USBCameraParams,
    cameraKey: CameraKey = 'primary'
  ): Promise<ConnectResult> {
    const startedAt = Date.now()
    cameraLog.connectStart(cameraKey, { deviceType, target: describeConnectTarget(deviceType, params) })
    /**
     * Report the outcome of this attempt. Fire-and-forget and fully guarded: booth
     * telemetry must never be able to fail a connect, so nothing here is awaited and
     * nothing here can throw into the caller.
     */
    const reportConnect = (outcome: 'ok' | 'recovered' | 'failed', reason?: unknown): void => {
      try {
        const store = useCameraStore.getState()
        const bound = cameraKey === 'primary' ? store.camera : store.secondaryCamera
        useBoothEventStore.getState().recordCameraConnect({
          outcome,
          cameraKey,
          deviceType,
          durationMs: Date.now() - startedAt,
          usedUsbReset: this._connectUsedReset[cameraKey],
          backend: (bound as { variant?: string } | null)?.variant ?? null,
          camera: typeof params === 'string' ? null : `${params.vendorId ?? ''}:${params.productId ?? ''}`,
          reason:
            reason === undefined
              ? null
              : reason instanceof Error
                ? reason.message.slice(0, 200)
                : String(reason).slice(0, 200)
        })
      } catch {
        // Observability is never worth breaking a camera connect for.
      }
      // The same outcome for the booth log, plus what the event leaves out: the path
      // taken, the error before its rewrite, and the body that actually bound.
      try {
        const store = useCameraStore.getState()
        const bound = cameraKey === 'primary' ? store.camera : store.secondaryCamera
        const backend =
          (bound as { variant?: string } | null)?.variant ??
          (this._connectPath[cameraKey] === 'native' ? 'native' : null)
        cameraLog.connectResult(outcome, cameraKey, {
          deviceType,
          path: this._connectPath[cameraKey],
          backend,
          durationMs: Date.now() - startedAt,
          usedUsbReset: this._connectUsedReset[cameraKey],
          requested: describeConnectTarget(deviceType, params),
          ...(reason === undefined ? {} : { reason: describeError(reason) }),
          ...(outcome === 'failed' && this._lastConnectRawError[cameraKey]
            ? { rawError: this._lastConnectRawError[cameraKey] }
            : {})
        })
        if (outcome !== 'failed' && deviceType === 'camera') {
          cameraLog.identify(cameraKey, {
            backend,
            path: this._connectPath[cameraKey],
            ...(usbIdentity(this._claimedUsbDevice[cameraKey]) ?? {})
          })
        }
      } catch {
        // see above
      }
    }

    // Flag OFF is the pre-2026-09-16 path, unchanged: every call connects directly.
    if (!isCameraRecoveryEnabled()) {
      try {
        const direct = await this.tryToConnectInner(deviceType, params, cameraKey)
        reportConnect(direct ? (this._connectUsedReset[cameraKey] ? 'recovered' : 'ok') : 'failed')
        if (direct?.type === 'camera') scheduleBoothBaseline(cameraKey)
        return direct
      } catch (err) {
        reportConnect('failed', err)
        throw err
      }
    }
    const token = connectTargetToken(deviceType, params)
    const inFlight = this._connectFlight.inFlightToken(cameraKey)
    // A connect that fails must leave the slot EMPTY, not half-populated. The DSLR branch
    // disconnects the webcam before it starts, so a failure used to leave the previous
    // `camera` handle standing with the old `deviceType` — and the settings modal reads
    // `webcam || camera`, so it reported a confident green "Connected" for a camera that
    // could never produce a frame. An honest empty slot shows "Not connected" and lets the
    // operator (or the monitor's prompt) act.
    const clearSlotOnFailure = (): void => {
      const st = useCameraStore.getState()
      if (cameraKey === 'primary') {
        if (st.camera || st.webcam) console.warn('[CONNECT] primary failed — clearing the slot')
        st.setCamera(null)
        st.setWebcam(null)
        st.setDeviceType(undefined)
      } else {
        if (st.secondaryCamera || st.secondaryWebcam)
          console.warn('[CONNECT] secondary failed — clearing the slot')
        st.setSecondaryCamera(null)
        st.setSecondaryWebcam(null)
        st.setSecondaryDeviceType(undefined)
      }
    }
    if (inFlight === token) {
      console.log(`[CONNECT] ${cameraKey} already connecting to this camera — joining`)
    } else if (inFlight) {
      console.log(`[CONNECT] ${cameraKey} switching camera — queued behind the attempt in flight`)
    }
    let result: ConnectResult
    try {
      result = await this._connectFlight.run(cameraKey, token, () =>
        this.tryToConnectInner(deviceType, params, cameraKey)
      )
    } catch (err) {
      clearSlotOnFailure()
      reportConnect('failed', err)
      throw err
    }
    if (!result) clearSlotOnFailure()
    reportConnect(result ? (this._connectUsedReset[cameraKey] ? 'recovered' : 'ok') : 'failed')
    if (result?.type === 'camera') scheduleBoothBaseline(cameraKey)
    return result
  }

  /** True while a connect for this slot is running (UI can show/disable accordingly). */
  isConnecting(cameraKey: CameraKey = 'primary'): boolean {
    return isCameraRecoveryEnabled() && this._connectFlight.isInFlight(cameraKey)
  }

  private async tryToConnectInner(
    deviceType: DeviceType,
    params: string | USBCameraParams,
    cameraKey: CameraKey = 'primary'
  ): Promise<ConnectResult> {
    // Claim this slot: any disconnect still unwinding must not write over what we set.
    if (isCameraRecoveryEnabled()) this._connectEpoch[cameraKey] += 1
    this._connectUsedReset[cameraKey] = false
    this._connectPath[cameraKey] = null
    this._lastConnectRawError[cameraKey] = null
    const store = useCameraStore.getState()
    const isPrimary = cameraKey === 'primary'
    // Fresh connection attempt starts with a clean preview-failure streak.
    this._consecutivePreviewFailures[cameraKey] = 0
    // A fresh connection is always awake — never inherit a stale parked flag.
    useCameraRestStore.getState().setParked(cameraKey, false)

    // In digital mode the libgphoto2/USB-DSLR path is never reachable —
    // the bundle doesn't ship the WASM, and the consumer has no UI for it.
    // Coerce 'camera' requests to webcam with an empty deviceId so the
    // browser picks its default. Any caller that still passes
    // USBCameraParams here is honoring a stale settings value.
    if (isDigitalMode() && deviceType === 'camera') {
      console.warn(
        '[uniCamUtils] Digital mode does not support USB-DSLR cameras; ' +
          'falling back to webcam.'
      )
      deviceType = 'webcam'
      params = ''
    }

    try {
      if (deviceType === 'webcam') {
        this._connectPath[cameraKey] = 'webcam'
        const currentWebcam = isPrimary ? store.webcam : store.secondaryWebcam
        if (currentWebcam) {
          await currentWebcam.disconnect()
          if (isPrimary) {
            store.setWebcam(null)
          } else {
            store.setSecondaryWebcam(null)
          }
        }

        // In digital + mobile, prefer the persisted facingMode (front-camera
        // default) over the deviceId (which is empty anyway because the
        // consumer never selected one). Desktop and kiosk use deviceId as
        // before.
        const useFacingMode = isDigitalMode() && isMobilePlatform()
        const webcamId = useFacingMode ? '' : (params as string)
        const facingMode = useFacingMode ? getDigitalFacingMode() : undefined
        const webcam = new Webcam()
        await webcam.connect(webcamId, facingMode)
        if (isPrimary) {
          store.setWebcam(webcam)
          store.setDeviceType(deviceType)
        } else {
          store.setSecondaryWebcam(webcam)
          store.setSecondaryDeviceType(deviceType)
        }
        this._isDisconnected[cameraKey] = false
        return { type: deviceType }
      } else if (deviceType === 'camera') {
        const currentWebcam = isPrimary ? store.webcam : store.secondaryWebcam
        if (currentWebcam) {
          await currentWebcam.disconnect()
          if (isPrimary) {
            store.setWebcam(null)
          } else {
            store.setSecondaryWebcam(null)
          }
        }

        // ── Native gphoto2 sidecar (flag `native_gphoto2`): the camera is owned by
        // a daemon child process of the main process, not by the renderer's WASM.
        // Gate on flag AND capability — a flagged booth on an older packaged main
        // (no IPC handlers) or without the daemon binary must silently stay on the
        // WASM path below. The whole WebUSB dance (grants, requestDevice, device
        // scoping, session listeners) is deliberately skipped: the daemon binds the
        // device through libusb/WinUSB directly and needs no browser grant, and the
        // renderer must NOT also open the device (WinUSB is single-application).
        if (isNativeGphoto2Enabled() && (await isNativeCameraAvailable())) {
          this._connectPath[cameraKey] = 'native'
          const priorCamera = isPrimary ? store.camera : store.secondaryCamera
          if (priorCamera) {
            // Release whichever backend held the slot (WASM drops its WebUSB claim;
            // a previous NativeCamera stops its daemon) before the daemon binds.
            await priorCamera.disconnect().catch(() => undefined)
          }
          const native = new NativeCamera({ moduleSlot: cameraKey })
          await native.connect()
          // The flag alone makes the installer fetch a daemon from S3, and that build
          // may predate the ops this renderer relies on. Seen in the field: an old
          // daemon engaged, every shutter press answered "unknown op", and the booth
          // silently ran a worse path than WASM. Ask once; if it is too old, stop it
          // and take the WASM path below exactly as an unflagged booth would.
          const protocol = await native.protocolVersion().catch(() => 1)
          if (protocol < NATIVE_DAEMON_MIN_PROTOCOL) {
            console.warn(
              `[NATIVE-CAMERA] ${cameraKey} daemon protocol ${protocol} < ${NATIVE_DAEMON_MIN_PROTOCOL}; staying on WASM`
            )
            this.logCameraActivity(
              'Native camera daemon is too old for this app; using the WASM backend',
              {
                cameraKey,
                protocol,
                required: NATIVE_DAEMON_MIN_PROTOCOL
              }
            )
            await native.disconnect().catch(() => undefined)
            if (isPrimary) store.setCamera(null)
            else store.setSecondaryCamera(null)
          } else {
            // The store's field is typed as the WASM Camera class, whose private
            // fields defeat structural typing; every consumer only calls the shared
            // seven-method surface, which NativeCamera implements 1:1.
            const asCamera = native as unknown as Camera
            if (isPrimary) {
              store.setCamera(asCamera)
              rememberLiveCamera(cameraKey, asCamera)
              store.setDeviceType(deviceType)
            } else {
              store.setSecondaryCamera(asCamera)
              store.setSecondaryDeviceType(deviceType)
            }
            this._isDisconnected[cameraKey] = false
            console.log(`[NATIVE-CAMERA] ${cameraKey} connected via gphoto2-daemon backend`)
            return { type: deviceType }
          }
        }

        const currentCamera = isPrimary ? store.camera : store.secondaryCamera
        if (!currentCamera) {
          // Everything below is WebUSB. Without it (Safari, Firefox, iPad) the first
          // `navigator.usb` call threw a bare TypeError; say what the operator can do.
          if (!isWebUsbAvailable()) throw new Error(NO_WEBUSB_MESSAGE)
          // Must be listening BEFORE requestDevice below: new mains emit the
          // session device list (Chromium-read serials) during every selection.
          initUsbSessionDeviceListener()
          // Per-key WASM module slot: primary and secondary DSLRs must not share one
          // libusb/libgphoto2 state (see camera.js — shared state crashes the WASM
          // when a second body connects). Reconnects reuse the same key's instance.
          // The BUILD (v1 / v2) is chosen per body once its USB vendor is known — v2 only
          // where it can help (Canon); see pickWasmVariant. Constructed at each connect
          // site below, after the USBDevice is in hand.
          const buildCamera = (device: USBDevice): Camera => {
            const body: CameraBodyIdentity = {
              vendorId: device.vendorId,
              productId: device.productId,
              serialNumber: device.serialNumber
            }
            let variant = pickWasmVariant(device.vendorId)
            if (variant === 'v2' && useCameraEngineBreakerStore.getState().isTripped(body)) {
              // This exact body already failed on this v2 build — the circuit breaker
              // dropped it to v1 so it heals in one reconnect instead of failing every
              // session until someone toggles the flag. A new v2 build (a fix) clears it.
              variant = 'v1'
              this.logCameraActivity('Camera engine: this body tripped v2 earlier; staying on v1 until a new build', {
                cameraKey,
                vendorId: device.vendorId,
                productId: device.productId,
                serialNumber: device.serialNumber ?? null
              })
            } else if (variant === 'v1' && isWasmV2Enabled()) {
              this.logCameraActivity('Camera engine: v2 flag is on but this body is not Canon; staying on v1', {
                cameraKey,
                vendorId: device.vendorId,
                productId: device.productId
              })
            }
            return new Camera({ moduleSlot: cameraKey, variant })
          }
          const filters: USBDeviceFilter[] = []
          const usbParams = typeof params === 'string' ? undefined : params
          // All same-series identity behavior rides the dual_camera feature flag.
          // Flag off ⇒ ignore any serial in the params and run the legacy filter set,
          // even if a serial-qualified selection value was persisted while it was on.
          const dualCam = isDualCameraEnabled()

          // ── Camera Reconnect v2 (flag `camera_reconnect_v2`, Phase 2 of
          // docs/camera-rest-plan.md): gesture-free soft reconnect. If the Electron
          // session still holds a WebUSB grant for a device matching this selection
          // (granted once since app boot and still plugged in), bind web-gphoto2 to it
          // directly — no requestDevice, no user-gesture requirement, no chooser
          // round-trip through the main process. Any miss/failure falls through to the
          // legacy requestDevice path below, so the worst case equals today's behavior.
          if (isReconnectV2Enabled()) {
            const grantedDevices = await navigator.usb.getDevices().catch(() => [] as USBDevice[])
            // A legacy selection is the bare productId as a string ("12927").
            const legacyProductId =
              typeof params === 'string' && /^\d+$/.test(params) ? Number(params) : undefined
            const granted = this.findGrantedUsbDevice(
              grantedDevices,
              usbParams,
              cameraKey,
              legacyProductId
            )
            if (granted) {
              this._connectPath[cameraKey] = 'soft'
              try {
                const camera = buildCamera(granted)
                console.log(`[RECONNECT-V2] Soft-reconnecting ${cameraKey} to granted device`, {
                  vendorId: granted.vendorId,
                  productId: granted.productId,
                  serialNumber: granted.serialNumber ?? null
                })
                this._claimedUsbDevice[cameraKey] = granted
                await withUsbDeviceScope(granted, () => camera.connect())
                // connect() resolving is NOT proof the body streams. A granted-device soft
                // reconnect frequently binds a STALE PTP session (the body slept / dropped its
                // link since the grant) — connect() succeeds but liveview never lands, which
                // surfaces downstream as "Camera connected but not responding". Verify one
                // frame; if it's dead, USB-reset the body (clears the half-open session in
                // software, no physical replug) and re-bind once. If it STILL won't stream,
                // throw so the catch below falls through to the chooser/requestDevice path
                // (which re-enumerates the device) — never hand back a camera that can't
                // produce a preview.
                if (!(await this.probeLiveviewAlive(camera))) {
                  console.warn(
                    `[RECONNECT-V2] Soft reconnect bound ${cameraKey} but liveview is dead; ` +
                      'USB-resetting and re-binding once'
                  )
                  await camera.disconnect().catch(() => undefined)
                  this._connectUsedReset[cameraKey] = true
                  await this.resetUsbDeviceBestEffort(granted)
                  await withUsbDeviceScope(granted, () => camera.connect())
                  if (!(await this.probeLiveviewAlive(camera))) {
                    throw new Error('Soft reconnect verified dead after USB reset')
                  }
                }
                if (isPrimary) {
                  store.setCamera(camera)
            rememberLiveCamera(cameraKey, camera)
                  rememberLiveCamera(cameraKey, camera)
                  store.setDeviceType(deviceType)
                } else {
                  store.setSecondaryCamera(camera)
                  store.setSecondaryDeviceType(deviceType)
                }
                this._isDisconnected[cameraKey] = false
                return {
                  type: deviceType,
                  deviceInfo: {
                    productName: granted.productName,
                    manufacturerName: granted.manufacturerName,
                    serialNumber: granted.serialNumber,
                    vendorId: granted.vendorId,
                    productId: granted.productId
                  }
                }
              } catch (softErr) {
                // Grant went stale (device unplugged/reset since it was granted) or the
                // WASM slot needs a rebuild — fall back to the chooser path, not a hard
                // failure. Clear the claim so the legacy path starts clean.
                console.warn(
                  '[RECONNECT-V2] Soft reconnect failed; falling back to requestDevice:',
                  softErr
                )
                cameraLog.softReconnectFailed(cameraKey, softErr, usbIdentity(granted) ?? undefined)
                this._claimedUsbDevice[cameraKey] = null
              }
            }
          }

          if (dualCam && usbParams?.vendorId && usbParams?.productId && usbParams?.serialNumber) {
            // Exact physical body requested. Do NOT add the broad PTP class filter —
            // filters are OR'ed, and with a same-series twin attached the class filter
            // would re-admit both bodies into the chooser list, leaving the main-process
            // ladder to guess. One exact filter ⇒ the list contains exactly our target.
            filters.push({
              vendorId: usbParams.vendorId,
              productId: usbParams.productId,
              serialNumber: usbParams.serialNumber
            })
          } else {
            filters.push({ classCode: 6, subclassCode: 1 })
            if (usbParams?.vendorId && usbParams?.productId) {
              filters.push({ vendorId: usbParams.vendorId, productId: usbParams.productId })
            }
          }

          if (window.electron && window.api.setUsbDeviceFilters) {
            window.api.setUsbDeviceFilters(filters)
          }

          // Tell the main process which serial(s) the OTHER camera key already claimed so
          // its selection ladder never answers this request with that body. This is what
          // makes an AMBIGUOUS selection (legacy bare-productId value — the only kind
          // available when node-usb can't read serials on Windows) resolve to the twin
          // instead of re-returning the primary. Old packaged mains have no listener on
          // this channel — harmless no-op, they keep today's behavior. Always sent (empty
          // when nothing is claimed) so stale exclusions never leak into later requests.
          // ALSO declare which camera key this request is for: the Canon R100 exposes NO
          // USB serial at all (Chromium reports it empty), so serial exclusion can never
          // engage for that model. The main process additionally excludes, by per-session
          // deviceId, any body it already granted to a DIFFERENT key — that path needs no
          // serial and is what actually separates two serial-less same-series bodies.
          if (window.electron && typeof window.api.send === 'function') {
            const otherKey: CameraKey = isPrimary ? 'secondary' : 'primary'
            const otherSerial = dualCam
              ? this._claimedUsbDevice[otherKey]?.serialNumber
              : undefined
            window.api.send(
              'set-usb-device-excluded-serials',
              otherSerial ? [otherSerial] : []
            )
            window.api.send('set-usb-request-context', { cameraKey })
          }

          this._connectPath[cameraKey] = 'chooser'
          console.log(`Requesting ${cameraKey} device with filters: `, filters)
          const usbDevice = await navigator.usb.requestDevice({ filters })

          console.log(`${cameraKey} device retrieved: `, usbDevice)
          const camera = buildCamera(usbDevice)

          if (dualCam) {
            // Guard against the chooser handing back the body the OTHER key already owns
            // (possible with broad filters + same-series twins, or a stale main process).
            const otherKey: CameraKey = isPrimary ? 'secondary' : 'primary'
            const otherDevice = this._claimedUsbDevice[otherKey]
            if (otherDevice && otherDevice === usbDevice) {
              throw new Error(
                `Kamera ini sudah dipakai sebagai kamera ${otherKey === 'primary' ? 'utama' : 'kedua'}. Pilih kamera fisik yang lain.`
              )
            }

            this._claimedUsbDevice[cameraKey] = usbDevice
            // Bind web-gphoto2 to exactly the requested body (see withUsbDeviceScope);
            // self-heals a stale session left by a previous process (see helper).
            await this.connectClearingStaleSession(cameraKey, camera, usbDevice, () =>
              withUsbDeviceScope(usbDevice, () => camera.connect())
            )
          } else {
            // Record the claimed body even with dual_camera OFF so teardown can be
            // scoped to exactly this device. Without this, disconnect() falls back
            // to closing EVERY granted USB device — which, on a machine running a
            // second app instance (e.g. the Bahela Mox twin-booth setup), tears
            // down the other instance's camera mid-session. The claim is purely
            // bookkeeping here; the legacy single-target connect() is unchanged.
            this._claimedUsbDevice[cameraKey] = usbDevice
            await this.connectClearingStaleSession(cameraKey, camera, usbDevice, () =>
              camera.connect()
            )
          }
          if (isPrimary) {
            store.setCamera(camera)
            store.setDeviceType(deviceType)
          } else {
            store.setSecondaryCamera(camera)
            store.setSecondaryDeviceType(deviceType)
          }

          this._isDisconnected[cameraKey] = false
          return {
            type: deviceType,
            deviceInfo: {
              productName: usbDevice.productName,
              manufacturerName: usbDevice.manufacturerName,
              serialNumber: usbDevice.serialNumber,
              vendorId: usbDevice.vendorId,
              productId: usbDevice.productId
            }
          }
        }
        this._isDisconnected[cameraKey] = false
        return { type: deviceType }
      }
      throw new Error('Invalid device type')
    } catch (error: unknown) {
      if (isPrimary) {
        useSettingsStore.getState().setSelectedCamera(undefined)
      } else {
        useSettingsStore.getState().setSelectedSecondaryCamera(undefined)
      }
      // A claim recorded before connect() failed would poison scoped teardown /
      // twin-exclusion checks; disconnect() below only clears it when a Camera
      // instance made it into the store.
      this._claimedUsbDevice[cameraKey] = null
      // Kept for the camera log before the message below replaces it.
      this._lastConnectRawError[cameraKey] = describeError(error)
      await this.disconnect(cameraKey, 'connect-failed')
      const errorMessage = connectErrorText(error)

      // The camera library's file is from a build the server no longer has. Reload
      // onto the live build once no guest is here; until then say so plainly.
      if (isStaleAssetError(errorMessage) && (await isNewerBuildLive())) {
        scheduleReloadWhenIdle('camera_library')
        throw new Error(
          'Gagal menyambungkan kamera: aplikasi baru saja diperbarui. Booth dimuat ulang otomatis saat kembali ke halaman awal.'
        )
      }

      if (
        errorMessage.includes('LIBUSB_ERROR_ACCESS') ||
        errorMessage.includes('Cannot use deleted val')
      ) {
        throw new Error(
          'Harap lepas dan sambungkan kembali kabel USB kamera Anda, lalu refresh aplikasi dan coba lagi'
        )
      } else if (errorMessage.includes('memory access out of bounds')) {
        throw new Error('Terlalu banyak percobaan gagal. Memuat ulang...')
      } else {
        throw new Error(`Gagal menyambungkan kamera: ${errorMessage}`)
      }
    }
  }

  /**
   * Detects if an image is too dark or black
   * @param blob - The image blob to analyze
   * @returns Object indicating if image is valid and what quality issues exist
   */
  private async detectImageQuality(
    blob: Blob
  ): Promise<{ isValid: boolean; issue?: 'dark' | 'black' | 'noise' | 'none' }> {
    try {
      // Create canvas to analyze image
      const canvas = document.createElement('canvas')
      const ctx = canvas.getContext('2d')
      if (!ctx) return { isValid: false, issue: 'noise' }

      const img = new Image()
      const url = URL.createObjectURL(blob)

      return new Promise((resolve) => {
        img.onload = (): void => {
          URL.revokeObjectURL(url)

          // Set canvas size to image size
          canvas.width = img.width
          canvas.height = img.height

          // Draw image to canvas
          ctx.drawImage(img, 0, 0)

          // Get image data for analysis
          const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height)
          const data = imageData.data

          let totalBrightness = 0
          let darkPixels = 0
          let blackPixels = 0
          const totalPixels = data.length / 4

          // Analyze each pixel
          for (let i = 0; i < data.length; i += 4) {
            const r = data[i]
            const g = data[i + 1]
            const b = data[i + 2]

            // Calculate brightness (using luminance formula)
            const brightness = 0.299 * r + 0.587 * g + 0.114 * b
            totalBrightness += brightness

            // Count dark pixels (brightness < 30)
            if (brightness < 30) {
              darkPixels++
            }

            // Count black pixels (brightness < 10)
            if (brightness < 10) {
              blackPixels++
            }
          }

          const avgBrightness = totalBrightness / totalPixels
          const darkPercentage = (darkPixels / totalPixels) * 100
          const blackPercentage = (blackPixels / totalPixels) * 100

          // Determine if image is problematic
          let issue: 'dark' | 'black' | 'noise' | 'none' = 'none'
          let isValid = true

          if (blackPercentage > 80) {
            issue = 'black'
            isValid = false
          } else if (darkPercentage > 70 || avgBrightness < 40) {
            issue = 'dark'
            isValid = false
          } else if (avgBrightness < 20) {
            issue = 'dark'
            isValid = false
          }

          resolve({ isValid, issue })
        }

        img.onerror = (): void => {
          URL.revokeObjectURL(url)
          resolve({ isValid: false, issue: 'noise' })
        }

        img.src = url
      })
    } catch (error) {
      console.error('Error analyzing image quality:', error)
      return { isValid: false, issue: 'noise' }
    }
  }

  async isDeviceResponding(cameraKey: CameraKey): Promise<ImageQualityResult> {
    if (this._isDisconnected[cameraKey]) {
      return { isResponding: false, isImageValid: false, qualityIssue: 'none' }
    }

    // Parked (camera-rest): the camera is intentionally sleeping with its connection
    // alive. Do NOT probe — a liveview read would silently restart liveview and raise
    // the mirror. Report healthy; a camera that died while parked is caught by the
    // wake verification instead.
    if (useCameraRestStore.getState().isParked(cameraKey)) {
      return { isResponding: true, isImageValid: true, qualityIssue: 'none' }
    }

    // A capture is actively running on this device. Don't issue a competing liveview read
    // (it would fight the shutter/preview over the single USB connection and manufacture the
    // very transient errors we're trying to avoid). Report healthy — an in-flight capture is
    // proof the camera is alive.
    if (this._captureDepth[cameraKey] > 0) {
      return { isResponding: true, isImageValid: true, qualityIssue: 'none' }
    }

    // A liveview frame arrived moments ago (the Preview loop pulls them continuously on
    // capture pages) — that IS the health check. Issuing another read here just doubles
    // USB traffic on the single PTP channel; the health monitor polls every 5s, so a
    // <4s-old frame guarantees at most one probe is ever added per period, and pages
    // with an active Preview never pay for a second reader at all.
    if (Date.now() - this._lastGoodPreviewAt[cameraKey] < 4000) {
      cameraLog.healthOk(cameraKey)
      return { isResponding: true, isImageValid: true, qualityIssue: 'none' }
    }

    const store = useCameraStore.getState()
    const isPrimary = cameraKey === 'primary'
    const deviceType = isPrimary ? store.deviceType : store.secondaryDeviceType
    const webcam = isPrimary ? store.webcam : store.secondaryWebcam
    const camera = isPrimary ? store.camera : store.secondaryCamera

    try {
      if (deviceType === 'webcam' && webcam) {
        const blob = await webcam.capturePreviewAsBlob()
        if (!blob) {
          cameraLog.healthFail(cameraKey, 'no-frame', undefined, { deviceType })
          return { isResponding: false, isImageValid: false, qualityIssue: 'none' }
        }

        // Analyze image quality
        const qualityResult = await this.detectImageQuality(blob)
        cameraLog.healthOk(cameraKey)
        return {
          isResponding: true,
          isImageValid: qualityResult.isValid,
          qualityIssue: qualityResult.issue
        }
      } else if (deviceType === 'camera' && camera) {
        const blob = await camera.capturePreviewAsBlob()
        if (!blob) {
          cameraLog.healthFail(cameraKey, 'no-frame', undefined, { deviceType })
          return { isResponding: false, isImageValid: false, qualityIssue: 'none' }
        }

        // Analyze image quality
        const qualityResult = await this.detectImageQuality(blob)
        cameraLog.healthOk(cameraKey)
        return {
          isResponding: true,
          isImageValid: qualityResult.isValid,
          qualityIssue: qualityResult.issue
        }
      }

      cameraLog.healthFail(cameraKey, 'no-device', undefined, {
        deviceType: deviceType ?? null,
        hasCamera: !!camera,
        hasWebcam: !!webcam
      })
      return { isResponding: false, isImageValid: false, qualityIssue: 'none' }
    } catch (err) {
      // This used to be swallowed whole, so "not responding" never came with a reason.
      cameraLog.healthFail(cameraKey, 'error', err, { deviceType })
      return { isResponding: false, isImageValid: false, qualityIssue: 'none' }
    }
  }

  async getConfig(cameraKey: CameraKey): Promise<unknown | undefined> {
    if (this._isDisconnected[cameraKey]) return undefined
    const store = useCameraStore.getState()
    const isPrimary = cameraKey === 'primary'
    const deviceType = isPrimary ? store.deviceType : store.secondaryDeviceType
    const webcam = isPrimary ? store.webcam : store.secondaryWebcam
    const camera = isPrimary ? store.camera : store.secondaryCamera

    if (deviceType === 'webcam' && webcam) {
      return webcam.getConfig()
    } else if (deviceType === 'camera' && camera) {
      return camera.getConfig()
    }
    return undefined
  }

  async setConfigValue(
    cameraKey: CameraKey,
    name: string,
    value: string | number | boolean,
    /**
     * `settle: false` skips the backends' 800ms post-write wait. That wait exists so
     * the settings UI can safely re-read a value the camera reports before it has
     * really applied it — irrelevant for an ACTION widget nothing reads back, where it
     * is pure added latency AND an extra 800ms of `_configMutationDepth` during which
     * liveview preview reads are made to wait. Default keeps the wait.
     */
    options?: { settle?: boolean }
  ): Promise<unknown | undefined> {
    if (this._isDisconnected[cameraKey]) return undefined
    const store = useCameraStore.getState()
    const isPrimary = cameraKey === 'primary'
    const deviceType = isPrimary ? store.deviceType : store.secondaryDeviceType
    const camera = isPrimary ? store.camera : store.secondaryCamera

    if (deviceType === 'webcam') {
      return undefined // Webcam doesn't support config changes
    } else if (deviceType === 'camera' && camera) {
      this._configMutationDepth[cameraKey] += 1
      try {
        return await camera.setConfigValue(name, value, options)
      } finally {
        // Give camera firmware a brief settle window after config write before liveview reads resume.
        await sleep(120)
        this._configMutationDepth[cameraKey] = Math.max(0, this._configMutationDepth[cameraKey] - 1)
      }
    }
    return undefined
  }

  /**
   * Write a camera event into the SESSION activity log — the one place a booth's story
   * is readable from the dashboard after the fact. The renderer console never leaves
   * the machine (logManager is main-process only), which is why a field report about
   * focus timing could not be checked against anything. Silent when there is no
   * session: the settings-modal test capture has none, and logActivity would otherwise
   * complain loudly about every one.
   */
  private logCameraActivity(message: string, details: Record<string, unknown>): void {
    const session = useSessionStateStore.getState()
    if (!session.id) return
    session.logActivity(message, details, 'capture')
  }

  /**
   * Steady-state prefocus skip reasons are true on every countdown of a session and
   * would drown the log; each is written once per session per camera. Keyed on the
   * session id so a new session starts with a clean slate.
   */
  private _loggedPrefocusSkips: Record<CameraKey, Set<string>> = {
    primary: new Set(),
    secondary: new Set()
  }

  /**
   * Take the body out of the autofocus-engaged state a prefocus drive left it in, right
   * before the shutter press. The half-press that follows then performs a REAL autofocus
   * from a lens that already sits at the subject, which is the whole point of prefocus —
   * without this release it performs none, and the photo is taken at a stale distance.
   *
   * Runs only when a drive actually happened since the last press, so nothing changes
   * for booths that never drove. Failure is never fatal: the press goes ahead exactly as
   * it would have without prefocus.
   */
  private async releasePrefocus(cameraKey: CameraKey): Promise<void> {
    if (!this._prefocusEngaged[cameraKey]) return
    this._prefocusEngaged[cameraKey] = false
    const store = useCameraStore.getState()
    const camera = cameraKey === 'primary' ? store.camera : store.secondaryCamera
    if (!camera || isPrefocusReleaseKnownUnsupported(camera)) return
    const startedAt = Date.now()
    try {
      await this.setConfigValue(cameraKey, PREFOCUS_RELEASE_CONFIG_NAME, 1, { settle: false })
      const releaseMs = Date.now() - startedAt
      console.log(`[PREFOCUS] ${cameraKey} released autofocus before the shutter in ${releaseMs}ms`)
      this.logCameraActivity('Prefocus: released autofocus before the shutter', { cameraKey, releaseMs })
    } catch (err) {
      if (isUnsupportedConfigError(err)) {
        markPrefocusReleaseUnsupported(camera)
        this.logCameraActivity('Prefocus: camera has no cancelautofocus widget, release skipped from now on', {
          cameraKey,
          error: err instanceof Error ? err.message : String(err)
        })
        return
      }
      console.warn(`[PREFOCUS] ${cameraKey} autofocus release failed; pressing anyway:`, err)
      this.logCameraActivity('Prefocus: autofocus release failed, pressing anyway', {
        cameraKey,
        afterMs: Date.now() - startedAt,
        error: err instanceof Error ? err.message : String(err)
      })
    }
  }

  /**
   * Drive ONE autofocus cycle now, ahead of a capture that is about to happen (flag
   * `camera_prefocus`). Called at the start of a capture countdown; see prefocus.ts for
   * why this is worth doing and why it is `autofocusdrive` rather than a half-press.
   *
   * Deliberately synchronous-looking and fire-and-forget: it returns `void`, never
   * throws, and is never awaited. A countdown must not be able to stall on the camera,
   * and a booth must never lose a paid session because a speed optimisation failed —
   * the capture path is completely unchanged whether this succeeds, fails or is skipped.
   *
   * Routed through `setConfigValue` rather than touching the Camera directly so it
   * inherits the existing coordination: the write is serialised on the camera's own op
   * queue, and `_configMutationDepth` makes liveview preview reads wait rather than
   * race it on a single-access USB channel.
   */
  prefocus = (explicitCameraKey?: CameraKey): void => {
    // Pages do not have to know which body they are about to shoot with: the session's
    // own `selectedCamera` is the same value the capture path reads, so a bare
    // `prefocus()` at a countdown start is always aimed at the right camera. Callers
    // that shoot BOTH bodies (the dual-camera kotak booth) name each one explicitly.
    const cameraKey: CameraKey =
      explicitCameraKey ??
      (useSessionStateStore.getState().userPreferences?.selectedCamera === 'secondary'
        ? 'secondary'
        : 'primary')
    const store = useCameraStore.getState()
    const isPrimary = cameraKey === 'primary'
    const camera = isPrimary ? store.camera : store.secondaryCamera
    const deviceType = isPrimary ? store.deviceType : store.secondaryDeviceType
    const settings = useSettingsStore.getState()
    const cameraSettings = isPrimary ? settings.cameraSettings : settings.secondaryCameraSettings
    const sessionId = useSessionStateStore.getState().id

    // Native: the daemon told us the model. WASM: the USB device this key is bound to
    // knows its vendor for certain — the persisted selection is only a fallback (it is a
    // bare productId on most booths, which gives null). Field lesson 2026-09-15: with the
    // selection alone a Sony resolved to null, the Sony gate below never fired, and the
    // Canon-only widget probes ran on it.
    const claimedDevice = this._claimedUsbDevice[cameraKey]
    const selectionVendor = vendorFromUsbVendorId(
      parseUsbSelectionValue(
        (isPrimary ? settings.selectedCamera : settings.selectedSecondaryCamera) ?? ''
      )?.vendorId
    )
    const vendor =
      camera instanceof NativeCamera
        ? vendorFromModel(camera.model)
        : (vendorFromUsbVendorId(claimedDevice?.vendorId) ?? selectionVendor)
    const vendorSource =
      camera instanceof NativeCamera
        ? 'model'
        : claimedDevice?.vendorId != null
          ? 'usb-device'
          : selectionVendor
            ? 'selection'
            : null
    const backend = !camera
      ? null
      : camera instanceof NativeCamera
        ? 'native'
        : camera.variant === 'v2'
          ? 'wasm-v2'
          : 'wasm'

    const decision = decidePrefocus({
      flagEnabled: isPrefocusEnabled(),
      useShutterCapture: cameraSettings.useShutterCapture,
      // Only where a no-AF press exists — native, or the v2 WASM build (see prefocus.ts).
      backendSupportsPrefocus: supportsNoAfPress(camera),
      vendor,
      focusMode: normaliseFocusMode(cameraSettings.focusMode),
      sessionId,
      lastPrefocusSessionId: this._lastPrefocusSessionId[cameraKey],
      deviceType,
      captureInFlight: this._shutterPressInFlight[cameraKey],
      recoveryInFlight: this._recoveringKeys.has(cameraKey),
      widgetSupported: camera ? !isPrefocusKnownUnsupported(camera) : false,
      backedOff: camera ? isPrefocusBackedOff(camera) : false,
      lastPrefocusAt: this._lastPrefocusAt[cameraKey],
      now: Date.now()
    })

    // Every decision is worth a line while the flag is on; with it off, or for a reason
    // that is true all session long (no widget, backed off), one line per session says
    // so and no more — an unflagged fleet must not pay a log entry per photo for this.
    if (cameraSettings.useShutterCapture) {
      const steady =
        !decision.run &&
        (decision.reason === 'flag-off' ||
          decision.reason === 'not-a-dslr' ||
          decision.reason === 'unsupported' ||
          decision.reason === 'wasm-backend' ||
          decision.reason === 'nikon-blocking-drive' ||
          decision.reason === 'sony-no-drive-widget' ||
          decision.reason === 'fuji-unbounded-drive' ||
          decision.reason === 'overlaps-capture' ||
          decision.reason === 'fixed-focus')
      const dedupeKey = `${sessionId ?? '-'}:${decision.reason ?? 'run'}`
      if (!steady || !this._loggedPrefocusSkips[cameraKey].has(dedupeKey)) {
        if (steady) this._loggedPrefocusSkips[cameraKey].add(dedupeKey)
        this.logCameraActivity(
          decision.run ? 'Prefocus: autofocus drive starting' : `Prefocus: skipped (${decision.reason})`,
          {
            cameraKey,
            reason: decision.reason ?? null,
            focusMode: normaliseFocusMode(cameraSettings.focusMode),
            flag: isPrefocusEnabled(),
            flagsLoaded: useFeatureFlagsStore.getState().loaded,
            parked: useCameraRestStore.getState().isParked(cameraKey),
            backend,
            vendor,
            vendorSource
          }
        )
      }
    }

    if (!decision.run || !camera) {
      // Only the reasons that describe something transient are worth a line. The steady
      // -state ones (flag off, preview capture, webcam, no widget on this body) are true
      // on every countdown for the whole run and would be pure log noise.
      if (
        decision.reason === 'capture-in-flight' ||
        decision.reason === 'recovery-in-flight' ||
        decision.reason === 'throttled'
      ) {
        console.log(`[PREFOCUS] Skipped ${cameraKey}: ${decision.reason}`)
      }
      return
    }

    // Stamped BEFORE the async work so two countdowns starting in the same tick cannot
    // both clear the throttle and queue two autofocus drives.
    this._lastPrefocusAt[cameraKey] = Date.now()
    this._lastPrefocusSessionId[cameraKey] = sessionId ?? null

    const drive = (async (): Promise<void> => {
      const startedAt = Date.now()
      this._prefocusDriveInFlight[cameraKey] = true
      try {
        // A capture countdown is as explicit a need for the camera as the capture
        // itself, so a parked body is woken here rather than skipped — the Preview that
        // mounted with the page is waking it too, and whichever gets there first wins.
        if (useCameraRestStore.getState().isParked(cameraKey)) {
          const wakeStartedAt = Date.now()
          const woke = await this.wakeCamera(cameraKey)
          this.logCameraActivity(woke ? 'Prefocus: camera was parked, woke it first' : 'Prefocus: camera was parked and would not wake', {
            cameraKey,
            wakeMs: Date.now() - wakeStartedAt
          })
          if (!woke) {
            // No drive happened, so the once-per-session claim staked above must not
            // stand — otherwise this session would never get its focus.
            this._lastPrefocusSessionId[cameraKey] = null
            return
          }
        }
        // settle:false — `autofocusdrive` is an action widget whose value is never read
        // back, so the 800ms UI settle would be 800ms of liveview held up at the very
        // moment the guest is watching the countdown start.
        // Flagged at DISPATCH, not on completion: a press that arrives while the drive is
        // still in flight queues behind it on the camera, and must still release — the
        // body is engaged from the moment DoAf is sent.
        this._prefocusEngaged[cameraKey] = true
        await this.setConfigValue(cameraKey, PREFOCUS_CONFIG_NAME, 1, { settle: false })
        const driveMs = Date.now() - startedAt
        console.log(`[PREFOCUS] ${cameraKey} autofocus drive took ${driveMs}ms`)
        this.logCameraActivity('Prefocus: autofocus drive finished', { cameraKey, driveMs })
      } catch (err) {
        // The optimistic claim above was to stop a double-fire, not to record success.
        // Releasing it means `once-per-session` retries on the next countdown instead of
        // spending the whole strip on a focus that never actually happened.
        this._lastPrefocusSessionId[cameraKey] = null
        if (isUnsupportedConfigError(err)) {
          // This body has no autofocusdrive widget. Remember it against the Camera
          // INSTANCE so a reconnect re-probes, and stop asking for the rest of this one.
          markPrefocusUnsupported(camera)
          console.log(
            `[PREFOCUS] ${cameraKey} has no '${PREFOCUS_CONFIG_NAME}' widget — prefocus disabled for this camera`
          )
          this.logCameraActivity('Prefocus: camera has no autofocusdrive widget, disabled for this camera', {
            cameraKey,
            error: err instanceof Error ? err.message : String(err)
          })
          return
        }
        // Anything else (busy, transient I/O) is worth knowing about but must not
        // escalate: the capture that follows is unaffected and still does its own AF.
        console.warn(`[PREFOCUS] ${cameraKey} failed; capture is unaffected:`, err)
        this.logCameraActivity('Prefocus: autofocus drive failed (capture unaffected)', {
          cameraKey,
          afterMs: Date.now() - startedAt,
          error: err instanceof Error ? err.message : String(err)
        })
      } finally {
        this._prefocusDriveInFlight[cameraKey] = false
      }
    })()
    // Held so the shutter can await it when the operator asked for that. Its rejection is
    // already handled inside, and `void` marks the default path: nobody awaits this.
    this._prefocusDrivePromise[cameraKey] = drive
    void drive
  }

  /** Did the operator ask this camera to wait for focus before firing? */
  private waitForFocusEnabled(cameraKey: CameraKey): boolean {
    const settings = useSettingsStore.getState()
    const cam =
      cameraKey === 'primary' ? settings.cameraSettings : settings.secondaryCameraSettings
    return cam?.waitForFocusBeforeCapture ?? WAIT_FOR_FOCUS_DEFAULT
  }

  /**
   * Hold the shutter until the countdown's autofocus drive finishes — only when the
   * operator asked for it, and never longer than FOCUS_WAIT_BUDGET_MS. A drive that
   * already finished, a camera with none in flight, or the setting off all return
   * immediately, so the default path costs nothing.
   */
  private async awaitFocusIfRequested(cameraKey: CameraKey): Promise<void> {
    if (!this.waitForFocusEnabled(cameraKey)) return
    const drive = this._prefocusDrivePromise[cameraKey]
    if (!drive || !this._prefocusDriveInFlight[cameraKey]) return
    const startedAt = Date.now()
    let timer: ReturnType<typeof setTimeout> | undefined
    // The drive settles its own errors; a failed focus must still let the shot happen.
    await Promise.race([
      drive.catch(() => undefined),
      new Promise<void>((resolve) => {
        timer = setTimeout(resolve, FOCUS_WAIT_BUDGET_MS)
      })
    ])
    if (timer) clearTimeout(timer)
    const waitedMs = Date.now() - startedAt
    const timedOut = this._prefocusDriveInFlight[cameraKey]
    console.log(
      `[PREFOCUS] ${cameraKey} shutter waited ${waitedMs}ms for focus` +
        (timedOut ? ` (gave up at ${FOCUS_WAIT_BUDGET_MS}ms and pressed anyway)` : '')
    )
    this.logCameraActivity('Focus: shutter waited for the autofocus drive', {
      cameraKey,
      waitedMs,
      timedOut
    })
  }

  /** Last mismatch reported per slot, so the log is one line rather than a flood. */
  private _lastTypeRepair: Record<CameraKey, string | null> = { primary: null, secondary: null }

  /**
   * The deviceType that actually has a handle behind it. Returns `stored` untouched when it
   * is consistent — and always when the flag is off, so the default fleet path is unchanged.
   * When `stored` names a missing handle while the other one is live, this corrects the
   * store and returns the usable type instead of letting every preview frame throw.
   */
  private resolveUsableDeviceType(
    cameraKey: CameraKey,
    stored: DeviceType | undefined,
    webcam: unknown,
    camera: unknown
  ): DeviceType | undefined {
    if (!isCameraRecoveryEnabled()) return stored
    const usable = usableDeviceType(stored, Boolean(webcam), Boolean(camera))
    if (usable === stored) {
      this._lastTypeRepair[cameraKey] = null
      return stored
    }
    const signature = `${stored ?? 'none'}->${usable}`
    if (this._lastTypeRepair[cameraKey] !== signature) {
      this._lastTypeRepair[cameraKey] = signature
      console.warn(
        `[CAMERA] ${cameraKey}: store said deviceType='${stored ?? 'none'}' but only a ${usable} is connected — using the ${usable} and correcting the store`
      )
    }
    const store = useCameraStore.getState()
    if (cameraKey === 'primary') store.setDeviceType(usable)
    else store.setSecondaryDeviceType(usable)
    return usable
  }

  capturePreview = async (
    cameraKey: CameraKey,
    options: {
      maxWidth?: number
      maxHeight?: number
      quality?: number
      rotation?: number // Allow passing rotation explicitly
      mirror?: boolean // Allow passing mirror explicitly
    } = {
      maxWidth:
        cameraKey === 'primary'
          ? useSettingsStore.getState().cameraSettings.previewQuality?.maxWidth
          : useSettingsStore.getState().secondaryCameraSettings.previewQuality?.maxWidth,
      maxHeight:
        cameraKey === 'primary'
          ? useSettingsStore.getState().cameraSettings.previewQuality?.maxHeight
          : useSettingsStore.getState().secondaryCameraSettings.previewQuality?.maxHeight,
      quality:
        cameraKey === 'primary'
          ? useSettingsStore.getState().cameraSettings.previewQuality?.quality
          : useSettingsStore.getState().secondaryCameraSettings.previewQuality?.quality
    }
  ): Promise<Blob | undefined> => {
    if (this._isDisconnected[cameraKey]) return undefined
    // Parked (camera-rest): return no frame WITHOUT counting a failure — a still-mounted
    // Preview loop polling while the camera rests must neither tear the camera down nor
    // restart liveview. Waking is explicit (wakeCamera) or capture-driven.
    if (useCameraRestStore.getState().isParked(cameraKey)) return undefined
    const store = useCameraStore.getState()
    const isPrimary = cameraKey === 'primary'
    const storedType = isPrimary ? store.deviceType : store.secondaryDeviceType
    const webcam = isPrimary ? store.webcam : store.secondaryWebcam
    const camera = isPrimary ? store.camera : store.secondaryCamera
    // Trust the HANDLE, not the label. The store can end up naming a device it does not
    // have — deviceType 'webcam' with no webcam but a live DSLR — and neither branch below
    // matches that pair, so every frame threw CameraUnavailableError for as long as the
    // page lived, while the settings modal still showed "Connected" (it reads
    // `webcam || camera`, which the stale handle satisfies). Field, 2026-09-17.
    //
    // The label goes stale because the transitions are not atomic: the DSLR branch of
    // tryToConnectInner disconnects and nulls the webcam FIRST and writes deviceType only
    // at the very end, after the USB/gphoto2 work, so any failure in between leaves the old
    // label standing over a missing handle. Several paths can produce the same mismatch, so
    // it is repaired where it is USED rather than in one of the producers.
    const deviceType = this.resolveUsableDeviceType(cameraKey, storedType, webcam, camera)

    // Get previewRotation and mirror settings - prioritize explicit options over store.
    // Preview.tsx always passes {rotation: 0, mirror: false} and handles transforms via CSS;
    // other callers (e.g. FrameBasedCapture canvas snapshot) rely on blob-level transforms.
    const settings = useSettingsStore.getState()
    const rotation =
      options?.rotation !== undefined
        ? options.rotation
        : isPrimary
          ? settings.cameraSettings.previewRotation
          : settings.secondaryCameraSettings.previewRotation
    const mirror =
      options?.mirror !== undefined
        ? options.mirror
        : isPrimary
          ? settings.cameraSettings.mirror
          : settings.secondaryCameraSettings.mirror

    try {
      let rawBlob: Blob | undefined
      if (deviceType === 'webcam' && webcam) {
        rawBlob = await webcam.capturePreviewAsBlob(
          options?.maxWidth,
          options?.maxHeight,
          options?.quality
        )
      } else if (deviceType === 'camera' && camera) {
        // Avoid racing liveview preview reads with in-flight camera config writes.
        if (this._configMutationDepth[cameraKey] > 0) {
          let waitCount = 0
          while (this._configMutationDepth[cameraKey] > 0 && waitCount < 20) {
            await sleep(50)
            waitCount += 1
          }
        }
        rawBlob = await captureDslrPreviewAsBlobWithPostShutterWaitAndRetry(camera, cameraKey)
      } else {
        // No branch matched: the store has a deviceType with no matching handle (or no
        // deviceType at all). Throwing routes this into the catch below so the
        // consecutive-failure counter advances and teardown/escalation can happen.
        // Returning undefined here instead would be a permanent silent dead end.
        throw new CameraUnavailableError(cameraKey, deviceType)
      }

      // Only apply expensive image processing if transforms are actually needed
      if (!rawBlob) return undefined

      // Got a real frame — the camera is alive; clear any prior transient-failure streak
      // and stamp freshness so isDeviceResponding can skip its own probe.
      this._consecutivePreviewFailures[cameraKey] = 0
      this._lastGoodPreviewAt[cameraKey] = Date.now()

      // Apply previewRotation/mirror only if needed (skipped when Preview.tsx passes rotation:0, mirror:false)
      if (rotation !== 0 || mirror) {
        rawBlob = await imageUtils.applyRotationMirroring(rawBlob, rotation, mirror)
      }

      return rawBlob
    } catch (error) {
      if (error instanceof CameraUnavailableError) {
        // No device at all. `capturePreview` is used by the live preview and filter
        // thumbnail paths, several of which are not wrapped in try/catch — so this must
        // NOT propagate, or a disconnect would take down the preview render loop.
        // Returning undefined keeps that contract; the still-capture path detects the
        // same condition up front in captureImage() and throws there instead, where the
        // caller is prepared for it.
        console.error('Preview capture failed (no usable device):', error)
        this._consecutivePreviewFailures[cameraKey] = 0
        return undefined
      }

      if (isGphotoCameraBusyError(error)) {
        console.warn('Preview capture failed (camera busy):', error)
        return undefined
      }

      // Transient USB/PTP I/O (and other non-"busy") errors: tolerate up to a few
      // CONSECUTIVE failures before tearing down the camera. The previous code nulled the
      // camera + cleared selectedCamera on the VERY FIRST such error, which killed a healthy
      // tethered camera on a momentary hiccup and stranded paid customers mid-capture
      // ("No image data captured" / "Camera hardware failure"). A real unplug fails every
      // read and crosses the threshold within a few ticks; a transient blip resets on the
      // next good frame (above).
      this._consecutivePreviewFailures[cameraKey] += 1
      const failures = this._consecutivePreviewFailures[cameraKey]
      const transient = isTransientCameraReadError(error)

      if (failures < MAX_CONSECUTIVE_PREVIEW_FAILURES) {
        console.warn(
          `Preview capture failed (${transient ? 'transient' : 'unclassified'} ${failures}/${MAX_CONSECUTIVE_PREVIEW_FAILURES}); keeping camera:`,
          error
        )
        return undefined
      }

      console.error(
        `Preview capture failed ${failures}x consecutively; treating camera as disconnected:`,
        error
      )
      this._consecutivePreviewFailures[cameraKey] = 0
      if (isPrimary) {
        useSettingsStore.getState().setSelectedCamera(undefined)
      } else {
        useSettingsStore.getState().setSelectedSecondaryCamera(undefined)
      }
      // Clear the handle AND the deviceType together. Nulling only the handle used to
      // leave `deviceType === 'camera' && camera === null`, which no capture dispatch can
      // match and no error path can escalate — see markDeviceDisconnected().
      useCameraStore.getState().markDeviceDisconnected(isPrimary ? 'primary' : 'secondary')
      return undefined
    }
  }

  /**
   * QR-optimized capture that bypasses all transformations for accurate QR code detection
   * This method returns raw, unprocessed images from both DSLR and webcam
   * Optimized for performance with balanced quality for QR detection
   */
  capturePreviewForQR = async (cameraKey: CameraKey): Promise<Blob | undefined> => {
    if (this._isDisconnected[cameraKey]) return undefined
    // Parked: no frames (and no silent liveview restart). QR scanning only runs
    // mid-session, where the camera is awake; a parked camera means no session.
    if (useCameraRestStore.getState().isParked(cameraKey)) return undefined
    const store = useCameraStore.getState()
    const isPrimary = cameraKey === 'primary'
    const deviceType = isPrimary ? store.deviceType : store.secondaryDeviceType
    const webcam = isPrimary ? store.webcam : store.secondaryWebcam
    const camera = isPrimary ? store.camera : store.secondaryCamera

    try {
      let rawBlob: Blob | undefined

      if (deviceType === 'webcam' && webcam) {
        // For webcam: Optimized resolution and quality for QR detection
        // Smaller size for faster processing while maintaining QR readability
        rawBlob = await webcam.capturePreviewAsBlob(
          1280, // Reduced from 1920 - still HD enough for QR codes
          720, // Reduced from 1080 - faster processing
          0.9 // Slight compression for faster transfer, still high quality
        )
      } else if (deviceType === 'camera' && camera) {
        // For DSLR: Get raw preview without any processing
        // DSLR previews are usually already optimized
        rawBlob = await camera.capturePreviewAsBlob()
      }

      // Return RAW blob without any transformations
      // No rotation, no mirroring, no image processing
      // This preserves pixel-perfect accuracy needed for QR detection
      return rawBlob
    } catch (error) {
      console.error('QR preview capture failed:', error)
      // Don't disconnect camera on QR capture failure - it's not critical
      return undefined
    }
  }

  /**
   * Flush queued PTP events before/after a shutter capture. PTP drivers (notably Sony Alpha)
   * emit a per-shot ObjectAdded event; if the event/object handle from the previous shot is not
   * drained, the next captureImageAsFile downloads a stale handle whose RAM buffer was already
   * consumed and deleted → PTP 0x2009 "Invalid Object Handle" (this is exactly why the 2nd shutter
   * fails while the 1st succeeds). consumeEvents() drains the queue and reports whether more events
   * remain; loop bounded and break when empty. Best-effort: a transient busy error here must never
   * fail the capture, so we swallow it.
   */
  private drainCameraEvents = async (camera: Camera, maxEvents = 16): Promise<void> => {
    for (let i = 0; i < maxEvents; i++) {
      try {
        const hadEvent = await camera.consumeEvents()
        if (!hadEvent) return
      } catch (err) {
        console.warn('[CAPTURE] Failed to drain PTP camera events:', err)
        return
      }
    }
  }

  captureImageAsFile = async (
    cameraKey: CameraKey,
    options: {
      /** Native only: press without autofocus (see NativeCamera.captureImageNoAf). */
      noAf?: boolean
    } = {}
  ): Promise<File | undefined> => {
    if (this._isDisconnected[cameraKey]) return undefined
    // Self-healing: a capture is an explicit need for the camera — if a path reaches
    // here while parked (session started without the Welcome wake), wake first.
    // No-op when not parked.
    await this.wakeCamera(cameraKey)
    const store = useCameraStore.getState()
    const isPrimary = cameraKey === 'primary'
    const deviceType = isPrimary ? store.deviceType : store.secondaryDeviceType
    const webcam = isPrimary ? store.webcam : store.secondaryWebcam
    const camera = isPrimary ? store.camera : store.secondaryCamera

    this._captureDepth[cameraKey] += 1
    try {
      if (deviceType === 'camera' && camera) {
        // Flush any stale ObjectAdded event left over from a prior shot (or liveview) so this
        // capture starts against a clean PTP event queue.
        await this.drainCameraEvents(camera)
        // Timed tightly around the driver call and nothing else: this one await is where
        // libgphoto2 half-presses, BLOCKS on autofocus, then releases the shutter. The
        // drains around it are ours and would dilute the signal. Recorded in a finally so
        // a press that dies on "Perhaps no focus?" still reports the AF budget it burned.
        const pressStartedAt = Date.now()
        let file: File | undefined
        try {
          if (options.noAf && supportsNoAfPress(camera) && !camerasWithoutNoAfPress.has(camera)) {
            try {
              file = await camera.captureImageNoAf()
              // The op cancels any engaged autofocus itself before pressing.
              this._prefocusEngaged[cameraKey] = false
              this.logCameraActivity('Shutter: pressed without autofocus', {
                cameraKey,
                backend: camera instanceof NativeCamera ? 'native' : 'wasm-v2'
              })
            } catch (err) {
              // The body or daemon cannot do a no-AF press. Release any engaged
              // autofocus FIRST — the ordinary capture half-presses with AF, and a body
              // still latched from the countdown drive would skip that AF and shoot at
              // a stale distance (the "only the first photo focuses" field report) —
              // then take the ordinary capture, exactly what an unflagged booth does.
              if (isUnsupportedConfigError(err)) {
                // This body cannot do the press at all (no widget / no MF choice) — a
                // permanent property, not a glitch. Remember it so the next shots skip
                // the probe instead of failing it again every time.
                camerasWithoutNoAfPress.add(camera)
                this.logCameraActivity(
                  'Shutter: this body has no no-AF press; using ordinary capture from now on',
                  { cameraKey, error: err instanceof Error ? err.message : String(err) }
                )
              } else {
                this.logCameraActivity('Shutter: no-AF press unavailable, using ordinary capture', {
                  cameraKey,
                  error: err instanceof Error ? err.message : String(err),
                  wasmError: wasmErrorOf(camera)
                })
              }
              await this.releasePrefocus(cameraKey)
              file = await camera.captureImageAsFile()
            }
          } else {
            file = await camera.captureImageAsFile()
          }
        } finally {
          recordShutterPressDuration(Date.now() - pressStartedAt)
        }
        // Drain the ObjectAdded event this capture just generated so the *next* shutter starts clean.
        // Deliberately OUTSIDE the finally: a failed press must keep reaching the retry
        // path with the event queue untouched, which is what `flash_capture_retry` owns.
        await this.drainCameraEvents(camera)
        return file
      } else if (deviceType === 'webcam' && webcam) {
        const settings = useSettingsStore.getState()
        const qualitySettings = isPrimary
          ? settings.cameraSettings.captureQuality
          : settings.secondaryCameraSettings.captureQuality
        return webcam.captureImageAsFile(
          qualitySettings?.maxWidth,
          qualitySettings?.maxHeight,
          qualitySettings?.quality
        )
      }
      return undefined
    } finally {
      this._captureDepth[cameraKey] = Math.max(0, this._captureDepth[cameraKey] - 1)
    }
  }

  captureImage = async (
    cameraKey: CameraKey = 'primary',
    filterFn?: FilterFn,
    frameRatio?: number,
    usePreview: boolean = true,
    onRawCaptured?: () => void,
    /**
     * Explicit normalized crop rect (unrotated sensor space). When provided it OVERRIDES the
     * centered `frameRatio` crop — the Bahelakotak booth uses this to capture the exact
     * off-center physical-box window each camera frames. Cropped in sensor space BEFORE
     * previewRotation/mirror, matching the live preview.
     */
    cropRect?: CropRect
  ): Promise<File | undefined> => {
    if (this._isDisconnected[cameraKey]) return undefined
    // Wake before the parked capturePreview guard can report "no frame" on the
    // usePreview path (and before the shutter path, via captureImageAsFile's own wake).
    await this.wakeCamera(cameraKey)

    // Pre-flight: refuse to "capture" when the store holds no usable device for the
    // current deviceType. Runs AFTER wakeCamera, so a merely parked camera is given its
    // chance to come back before we declare the device missing.
    //
    // Without this the call quietly produced `undefined` forever — indistinguishable
    // from a transient miss — and the capture loop retried it ~every 15s for hours while
    // the customer watched "Retrying in a moment…". Throwing makes it a classifiable
    // hardware failure the loop can escalate on.
    assertCaptureDeviceAvailable(cameraKey)

    this._captureDepth[cameraKey] += 1
    // The shutter path logs its own failure with more detail; the catch below must not repeat it.
    let failureLogged = false
    try {
      let imageBlob: Blob
      const isPrimary = cameraKey === 'primary'
      const settings = useSettingsStore.getState()
      const qualitySettings = isPrimary
        ? settings.cameraSettings.captureQuality
        : settings.secondaryCameraSettings.captureQuality
      // Bake the same rotation + mirror as live Preview (previewRotation), so stills, Filter, and
      // strip video match what the user saw. captureRotation remains available for hardware paths
      // that bypass the preview pipeline.
      const previewRotation = isPrimary
        ? settings.cameraSettings.previewRotation
        : settings.secondaryCameraSettings.previewRotation
      const mirror = isPrimary
        ? settings.cameraSettings.mirror
        : settings.secondaryCameraSettings.mirror

      if (usePreview) {
        // Fetch raw preview blob without any transforms — previewRotation + mirror applied below
        const rawBlob = await this.capturePreview(cameraKey, {
          maxWidth: qualitySettings?.maxWidth,
          maxHeight: qualitySettings?.maxHeight,
          quality: qualitySettings?.quality,
          rotation: 0,
          mirror: false
        })
        if (!rawBlob) {
          this.recordCaptureError(cameraKey, 'preview returned no frame', 'no-frame')
          cameraLog.captureFail(cameraKey, undefined, { mode: 'preview', kind: 'no-frame' })
          return undefined
        }
        cameraLog.captureOk(cameraKey, { mode: 'preview', bytes: rawBlob.size })
        imageBlob = rawBlob
      } else {
        // Physical shutter. Retry IN PLACE on focus/busy failures before surfacing a
        // failed capture: an AF miss ("Perhaps no focus?") resolves by re-pressing the
        // shutter after a short settle — tearing the connection down (what the pages'
        // "hardware failure" recovery does) turns a 1-second refocus into a multi-minute
        // reconnect incident.
        let file: File | undefined
        recordShutterAttempt()
        const captureStartedAt = Date.now()
        // Did this press arrive while a prefocus drive was still running? That is the
        // one way prefocus can cost a shot time instead of saving it, and two in a row
        // switches it off for this camera.
        const pressStore = useCameraStore.getState()
        const pressCamera = isPrimary ? pressStore.camera : pressStore.secondaryCamera
        const overlappedPrefocusDrive = this._prefocusDriveInFlight[cameraKey]
        // With "wait for focus" on, an overlap is the POINT, not a cost: the operator
        // asked the shutter to wait. Counting it as a fault would back prefocus off after
        // two shots and quietly disable the very setting they turned on.
        const waitsForFocus = this.waitForFocusEnabled(cameraKey)
        if (waitsForFocus) await this.awaitFocusIfRequested(cameraKey)
        if (!waitsForFocus && pressCamera && recordPrefocusOverlap(pressCamera, overlappedPrefocusDrive)) {
          this.logCameraActivity('Prefocus: backing off for this camera — drive still running at the shutter twice in a row', { cameraKey })
          console.warn(
            `[PREFOCUS] ${cameraKey} shutter kept arriving while the autofocus drive was still ` +
              `running; backing off for this camera — prefocus is delaying shots here, not shortening them`
          )
        }
        // On the native backend a countdown drive means the lens is ALREADY at the
        // subject, so press without autofocus: the daemon's capture_no_af cancels the
        // engaged AF itself and fires with the AF flag off — the release below is then
        // redundant and skipped. WASM has no such press (its only capture is
        // gp_camera_capture, which always half-presses with AF), so there the drive
        // never runs in the first place (prefocus is native-only).
        // Where the backend can press without autofocus, ALWAYS press that way — not only
        // after a drive. The Focus setting decides when the lens is focused (every photo /
        // once per session / never); the press itself never asks the camera to focus, so
        // a lens focused once stays put for the whole strip, and "Fixed" really is fixed.
        // v1 WASM has no such press and takes the ordinary capture, unchanged.
        const noAfPress = supportsNoAfPress(pressCamera)
        if (!noAfPress) {
          // Must precede the press: see releasePrefocus. Queued behind any drive still in
          // flight, so an unfinished hunt is cancelled rather than raced.
          await this.releasePrefocus(cameraKey)
        }
        this._shutterPressInFlight[cameraKey] = true
        let lastPressMs = 0
        let pressAttempts = 1
        for (let attempt = 0; ; attempt++) {
          const pressStartedAt = Date.now()
          try {
            file = await this.captureImageAsFile(cameraKey, { noAf: noAfPress })
            lastPressMs = Date.now() - pressStartedAt
            pressAttempts = attempt + 1
            recordShutterSuccess(attempt)
            recordShutterCaptureDuration(Date.now() - captureStartedAt)
            break
          } catch (err) {
            const kind = classifyCaptureError(err)
            recordShutterPressFailure(kind)
            const delays = getShutterRetryDelays(kind)
            if (delays && attempt < delays.length) {
              const delayMs = delays[attempt]
              console.warn(
                `[CAPTURE] Shutter attempt ${attempt + 1} failed (${kind}); retrying in ${delayMs}ms:`,
                err instanceof Error ? err.message : err
              )
              cameraLog.captureRetry(cameraKey, err, {
                kind,
                attempt: attempt + 1,
                pressMs: Date.now() - pressStartedAt,
                retryInMs: delayMs,
                wasmError: wasmErrorOf(pressCamera) ?? null
              })
              await sleep(delayMs)
              if (kind === 'busy' && isFlashCaptureRetryEnabled()) {
                // A dirty PTP event queue is the leading cause of busy on re-press; the
                // settle above also gives a recharging flash time to report ready. Drain
                // what accumulated during the wait so the retry starts clean.
                const retryStore = useCameraStore.getState()
                const retryCamera = isPrimary ? retryStore.camera : retryStore.secondaryCamera
                if (retryCamera) await this.drainCameraEvents(retryCamera)
              }
              continue
            }
            this._shutterPressInFlight[cameraKey] = false
            recordShutterFailure()
            this.recordCaptureError(cameraKey, err, kind)
            this.logCameraActivity('Shutter: capture failed', {
              cameraKey,
              kind,
              attempts: attempt + 1,
              totalMs: Date.now() - captureStartedAt,
              overlappedPrefocusDrive,
              error: err instanceof Error ? err.message : String(err),
              wasmError: wasmErrorOf(pressCamera)
            })
            cameraLog.captureFail(cameraKey, err, {
              mode: 'shutter',
              kind,
              attempts: attempt + 1,
              totalMs: Date.now() - captureStartedAt,
              noAf: noAfPress,
              backend: (pressCamera as { variant?: string } | null)?.variant ?? null,
              wasmError: wasmErrorOf(pressCamera) ?? null
            })
            failureLogged = true
            // Circuit breaker: a capture that exhausted its retries on a v2 body is the
            // strong signal (the ordinary capture, which works on that body under v1,
            // also failed here). Trip THIS body so the recovery that follows rebinds it
            // on v1 — one bad capture, then healed, instead of a session-long loop. Only
            // v2 bodies trip; v1 failures are ordinary hardware blips, not an engine fault.
            if (pressCamera instanceof Camera && pressCamera.variant === 'v2') {
              const device = this._claimedUsbDevice[cameraKey]
              if (device) {
                const reason = wasmErrorOf(pressCamera) ?? (err instanceof Error ? err.message : String(err))
                // Deliberately fire-and-forget: the trip decision needs one async USB
                // question (below) and must not delay the failure the caller is handling.
                void this.tripCameraEngineIfItWasTheEngine(cameraKey, device, reason)
              }
            }
            throw err
          }
        }
        // The press is over the moment we hold the file; everything after this is CPU work
        // and the next countdown (started from onRawCaptured below) may prefocus freely.
        this._shutterPressInFlight[cameraKey] = false
        if (!file) return undefined
        markLastShutterCompleted(cameraKey)
        // `pressMs` is the single libgphoto2 call that half-presses, waits on autofocus
        // and releases; `totalMs` adds our drains and any retry backoff. The page's own
        // "Downloaded photo" entry follows once transforms are done, so the gap between
        // this line and that one is pure CPU work.
        this.logCameraActivity('Shutter: photo captured', {
          cameraKey,
          pressMs: lastPressMs,
          totalMs: Date.now() - captureStartedAt,
          overlappedPrefocusDrive,
          fileBytes: file.size
        })
        cameraLog.captureOk(cameraKey, {
          mode: 'shutter',
          attempts: pressAttempts,
          pressMs: lastPressMs,
          totalMs: Date.now() - captureStartedAt,
          noAf: noAfPress,
          fileBytes: file.size
        })
        imageBlob = new Blob([await file.arrayBuffer()])
      }

      if (!imageBlob) return undefined

      // The raw frame is now in hand and the camera/shutter is free again. Signal the
      // caller so it can resume its countdown immediately — everything below (resize,
      // crop, filter, rotation) is pure CPU work that can finish in the background
      // without holding up the next shot.
      //
      // This is also the only honest moment to tell the GUEST the photo happened. With
      // physical-shutter capture the frame can land well after the countdown reached
      // zero (libgphoto2 blocks on autofocus inside the capture call), so a cue tied to
      // the countdown would be wrong about the one thing operators say guests misread.
      // Silent unless the project asked for it and the flag is on.
      playCaptureCue()
      onRawCaptured?.()

      const currentCameraStore = useCameraStore.getState()
      const currentDeviceType = isPrimary
        ? currentCameraStore.deviceType
        : currentCameraStore.secondaryDeviceType
      let blobToProcess: Blob = imageBlob
      const camera = isPrimary ? currentCameraStore.camera : currentCameraStore.secondaryCamera

      if (currentDeviceType === 'camera' && camera) {
        blobToProcess = await imageUtils.resize(imageBlob, {
          maxWidth: qualitySettings?.maxWidth,
          maxHeight: qualitySettings?.maxHeight,
          quality: qualitySettings?.quality,
          resizeQuality: qualitySettings?.resizeQuality
        })
      }

      // This camera's file format (Camera Settings → Photo Format), or the booth default
      // (capture_jpeg_default) when the operator never picked one.
      const encoding = captureEncodingFor(
        resolveCaptureFormat(
          isPrimary
            ? settings.cameraSettings.captureFormat
            : settings.secondaryCameraSettings.captureFormat
        ),
        qualitySettings?.quality
      )
      const needsRotationMirroring = previewRotation !== 0 || mirror

      // Crop + filter in sensor space first (L2 frameRatio), then apply previewRotation + mirror.
      // Order is intentional: applying rotation *before* crop used to over-crop portrait
      // bitmaps when combined with 90°/270° and a landscape frame ratio (zoomed-in look).
      // Only the last pass encodes the chosen format; a pass that is decoded again stays
      // lossless, so a JPEG photo is compressed once here, not twice.
      let processedBlob = await imageUtils.processPreview(blobToProcess, {
        ratio: frameRatio,
        filter: filterFn,
        cropRect,
        encoding: needsRotationMirroring ? LOSSLESS_ENCODING : encoding
      })

      if (needsRotationMirroring) {
        processedBlob = await imageUtils.applyRotationMirroring(
          processedBlob,
          previewRotation,
          mirror,
          encoding
        )
      }

      // Legacy post-capture settle delay. Skipped when the caller manages pacing via
      // onRawCaptured (it has already resumed its countdown, so this would only add
      // dead time before the processed file is returned).
      if (!onRawCaptured) {
        await new Promise((resolve) => setTimeout(resolve, 1000))
      }

      return new File([processedBlob], `dreambooth-image.${encoding.extension}`, {
        type: encoding.type
      })
    } catch (error) {
      // Record before swallowing: this catch used to erase all diagnostic detail, which
      // is why 100% of fleet capture failures reached the activity logs as the generic
      // "No image data captured." Pages now read getLastCaptureError() for the truth.
      this.recordCaptureError(cameraKey, error)
      console.error('Image capture failed:', error)
      if (!failureLogged) cameraLog.captureFail(cameraKey, error, { stage: 'capture or processing' })
      // Propagate "there is no camera" instead of flattening it to `undefined`.
      // Swallowing it here is what made every downstream hardware classification dead
      // code: the caller could not tell a missing device from a bad frame, so it kept
      // retrying a capture that could never succeed.
      if (error instanceof CameraUnavailableError) throw error
      return undefined
    } finally {
      this._captureDepth[cameraKey] = Math.max(0, this._captureDepth[cameraKey] - 1)
    }
  }

  /**
   * The camera goodbye on app close. Releases every connected camera as fast as is
   * safe, bounded by `budgetMs`, and resolves the count. Called by the graceful-quit
   * handler (see gracefulQuit.ts) while main holds the window open for us.
   *
   * Differs from disconnect() on purpose: no toasts, no 1.5 s of settle per camera, no
   * store churn beyond nulling the instance — the process is about to die, so all that
   * matters is that `gp_camera_exit` is issued and its USB round-trips land (Canon:
   * SetRemoteMode(1) + CloseSession — the body's display comes back on and the next
   * launch connects without a replug), then the WebUSB device is closed. Both cameras
   * are released in parallel to stay inside the budget. Every step is best-effort: a
   * camera that will not answer costs its share of the budget, never a hang.
   */
  /**
   * Blame v2 for a failed capture ONLY when v2 is a plausible cause.
   *
   * A capture can fail for reasons that have nothing to do with which camlib is driving
   * it — above all the cable being pulled or the body powering off, which fails EVERY
   * engine identically. Tripping on those exiles a perfectly good camera from the faster
   * engine until someone notices. That is exactly what happened in the field
   * (2026-09-16): the operator unplugged USB on purpose to test a failure, and v2 was
   * tripped for it — then ran eight flawless captures once the trip was cleared.
   *
   * The discriminator is whether the body is still attached at all. WebUSB drops an
   * unplugged device from `getDevices()`, so a device that is gone means "the cable/
   * power went", not "this engine is broken". When we cannot tell, we do NOT trip —
   * a missed trip costs one more failed capture, a wrong trip costs the faster engine.
   */
  private async tripCameraEngineIfItWasTheEngine(
    cameraKey: CameraKey,
    device: USBDevice,
    reason: string
  ): Promise<void> {
    const stillAttached = await navigator.usb
      .getDevices()
      .then((devices) => devices.includes(device))
      .catch(() => false)
    if (!stillAttached) {
      this.logCameraActivity('Camera engine: capture failed but the body is gone — not blaming v2', {
        cameraKey,
        vendorId: device.vendorId,
        productId: device.productId,
        reason
      })
      return
    }
    useCameraEngineBreakerStore.getState().trip(
      {
        vendorId: device.vendorId,
        productId: device.productId,
        serialNumber: device.serialNumber
      },
      reason
    )
    this.logCameraActivity('Camera engine: v2 tripped for this body; next connect uses v1', {
      cameraKey,
      vendorId: device.vendorId,
      productId: device.productId,
      serialNumber: device.serialNumber ?? null,
      reason
    })
    // Surface it now, not at the 6-hourly snapshot, so the fleet view sees the trip
    // while the operator is still on the booth.
    void sendAppSettingsTelemetry({
      projectId: useSessionStateStore.getState().projectId || undefined
    }).catch(() => undefined)
  }

  async releaseAllForQuit(budgetMs: number = RENDERER_RELEASE_BUDGET_MS): Promise<number> {
    const store = useCameraStore.getState()
    // The STORE is what the UI displays, not what holds a body. The health monitor clears
    // it after two unanswered probes so the reconnect prompt can appear, without calling
    // disconnect() — correct for it, since the camera is not answering and a blocking
    // gp_camera_exit there would freeze the page, but it orphans a Camera that still holds
    // the USB claim and an open PTP session. Reading only the store is why closing from the
    // welcome screen made no click while closing from the settings modal (where a preview
    // keeps the store populated) did, and why the body was left in remote mode so the next
    // launch reconnected "often, but sometimes not". Release the union of both.
    const fromStore = (['primary', 'secondary'] as CameraKey[]).map((cameraKey) => ({
      cameraKey,
      camera: (cameraKey === 'primary' ? store.camera : store.secondaryCamera) as
        | ReleasableCamera
        | null
    }))
    const byKey = new Map<CameraKey, ReleasableCamera>()
    for (const { cameraKey, camera } of fromStore) if (camera) byKey.set(cameraKey, camera)
    if (isCameraRecoveryEnabled()) {
      // An orphan only counts when the slot has nothing live — never displace the camera
      // the store is currently showing.
      for (const { cameraKey, camera } of getLiveCameras()) {
        if (!byKey.has(cameraKey)) {
          byKey.set(cameraKey, camera)
          console.warn(`[GOODBYE] ${cameraKey} was orphaned (cleared from the store without a release) — releasing it anyway`)
        }
      }
    }
    const targets = [...byKey.entries()].map(([cameraKey, camera]) => ({ cameraKey, camera }))
    if (targets.length === 0) return 0

    const deadline = Date.now() + budgetMs
    const remaining = (): number => Math.max(0, deadline - Date.now())
    const settle = (ms: number): Promise<void> =>
      new Promise((resolve) => setTimeout(resolve, Math.min(ms, remaining())))

    await Promise.all(
      targets.map(async ({ cameraKey, camera }) => {
        // Stop new work first: capture and preview guard on this flag, so nothing races
        // the teardown with a fresh USB op.
        this._isDisconnected[cameraKey] = true
        useCameraRestStore.getState().setParked(cameraKey, false)
        // Let an in-flight preview frame finish before the context goes away. Deleting
        // the context under a live preview read is what skipped gp_camera_exit — the
        // camera never left remote mode and the operator heard no click.
        await settle(RENDERER_PREVIEW_SETTLE_MS)
        try {
          // gp_camera_exit (WASM) / daemon 'disconnect' op (native). Bounded: a body
          // that never answers must not eat the whole budget.
          await Promise.race([camera.disconnect(), settle(remaining())])
        } catch {
          // Best-effort: the OS closes the pipe anyway; we tried to say goodbye.
        }
        // Give the asyncified destructor's USB round-trips time to land.
        await settle(RENDERER_RELEASE_SETTLE_MS)
        const device = this._claimedUsbDevice[cameraKey]
        if (device?.opened) {
          await usbCall('releaseInterface(0) [quit]', () => device.releaseInterface(0), 500)
          await usbCall('close [quit]', () => device.close(), 500)
        }
        this._claimedUsbDevice[cameraKey] = null
        forgetLiveCamera(cameraKey, camera)
        if (cameraKey === 'primary') store.setCamera(null)
        else store.setSecondaryCamera(null)
        console.log(`[GOODBYE] ${cameraKey} camera released`)
      })
    )
    return targets.length
  }

  /**
   * The cable for this key was physically pulled. Drop the session locally.
   *
   * Strictly no USB I/O: the device is gone, so `gp_camera_exit` / `releaseInterface`
   * would issue round-trips nothing can answer — exactly the kind of call that blocks
   * the renderer thread. Everything here is local bookkeeping, so the next connect
   * starts clean via the normal stale-session recovery instead of reusing a module
   * whose libusb handle points at a device that no longer exists.
   */
  /** Snapshot of what each key currently claims — for the unplug watcher. */
  getClaimedUsbDevices(): Record<CameraKey, USBDevice | null> {
    return { ...this._claimedUsbDevice }
  }

  handlePhysicalUnplug(cameraKey: CameraKey): void {
    const isPrimary = cameraKey === 'primary'
    const store = useCameraStore.getState()
    const camera = isPrimary ? store.camera : store.secondaryCamera
    cameraLog.disconnect(cameraKey, 'usb-unplug', usbIdentity(this._claimedUsbDevice[cameraKey]) ?? undefined)

    // Stop new work first — preview, capture and the health probe all guard on this.
    this._isDisconnected[cameraKey] = true
    this._consecutivePreviewFailures[cameraKey] = 0
    this._prefocusEngaged[cameraKey] = false
    // A parked flag must never outlive its connection, or the next one starts "asleep".
    useCameraRestStore.getState().setParked(cameraKey, false)
    this._claimedUsbDevice[cameraKey] = null

    // Evict the WASM module bound to the dead device. This is a map delete, not a
    // camera call, so it is safe with the cable out.
    try {
      const evictable = camera as { discardModule?: () => void } | null
      evictable?.discardModule?.()
    } catch {
      // Best effort — a failed eviction must not stop the rest of the cleanup.
    }

    if (isPrimary) store.setCamera(null)
    else store.setSecondaryCamera(null)

    // Free this key's grant in the main process so the body is selectable again once
    // it is plugged back in.
    if (window.electron && typeof window.api?.send === 'function') {
      window.api.send('usb-camera-released', { cameraKey })
    }
  }

  /**
   * @param reason why, for the camera log only. Callers that do not say are logged as
   *   'unspecified' (mostly the operator's Settings screens).
   */
  async disconnect(
    cameraKey: CameraKey = 'primary',
    reason: DisconnectReason = 'unspecified'
  ): Promise<void> {
    // A parked flag must never outlive the connection it parked (a stale flag would
    // make the NEXT connection start "asleep": no previews, healthy-looking monitor).
    useCameraRestStore.getState().setParked(cameraKey, false)
    const store = useCameraStore.getState()
    const isPrimary = cameraKey === 'primary'
    const camera = isPrimary ? store.camera : store.secondaryCamera
    cameraLog.disconnect(cameraKey, reason, {
      hadCamera: !!camera,
      deviceType: (isPrimary ? store.deviceType : store.secondaryDeviceType) ?? null
    })
    const setCamera = isPrimary ? store.setCamera : store.setSecondaryCamera
    const setDeviceType = isPrimary ? store.setDeviceType : store.setSecondaryDeviceType
    // Everything below this point runs after ~1.5s of sleeps and a gphoto2 exit. If a
    // connect claimed the slot meanwhile, this disconnect is stale and must not write.
    const epoch = this._connectEpoch[cameraKey]
    const stillOurs = (): boolean =>
      !isCameraRecoveryEnabled() || this._connectEpoch[cameraKey] === epoch

    // Void this key's grant in the main process so its body becomes selectable by the
    // other key again. Sent unconditionally (not just when a Camera instance exists):
    // a grant is recorded the moment select-usb-device answers, even if the gphoto2
    // connect afterwards failed and this disconnect is the error-path cleanup.
    if (window.electron && typeof window.api?.send === 'function') {
      window.api.send('usb-camera-released', { cameraKey })
    }

    try {
      if (camera) {
        this._isDisconnected[cameraKey] = true
        this._consecutivePreviewFailures[cameraKey] = 0
        await new Promise((resolve) => setTimeout(resolve, 1000))
        await camera.disconnect()
        forgetLiveCamera(cameraKey, camera as unknown as ReleasableCamera)
        if (stillOurs()) setCamera(null)
        await new Promise((resolve) => setTimeout(resolve, 500))

        // Scoped teardown — close only the USB device THIS camera key claimed, and
        // never one the OTHER key (or a second app instance sharing this machine's
        // WebUSB grants) is using. The pre-fix code closed EVERY device from
        // navigator.usb.getDevices() whenever dual_camera was off; on a shared
        // machine that tore down another app instance's camera mid-session — the
        // Bahela Mox "sering disconnect" incident. Scoping is correct regardless of
        // the flag, so it is no longer gated on it.
        const claimed = this._claimedUsbDevice[cameraKey]
        const otherKey: CameraKey = isPrimary ? 'secondary' : 'primary'
        // A newer connect may already have claimed a device on this slot — closing it
        // here would tear down the connection the operator is waiting on.
        const mayCloseDevices = stillOurs()
        const otherClaimed = this._claimedUsbDevice[otherKey]
        const devices = await navigator.usb.getDevices()
        for (const device of devices) {
          // A newer connect already owns this slot — closing its device here would tear
          // down the connection the operator is waiting on.
          if (!mayCloseDevices) continue
          // Never touch a device another key/instance owns.
          if (otherClaimed && device === otherClaimed) continue
          // If we know exactly what this key claimed, close only that. If not
          // (webcam path, or a claim we never recorded) fall back to closing the
          // rest — the guard above still protects the other key's device.
          if (claimed && device !== claimed) continue
          try {
            if (device.opened) {
              await device.releaseInterface(0)
              await device.close()
            }
          } catch (deviceErr) {
            console.warn('Error releasing USB device:', deviceErr)
          }
        }
        if (stillOurs()) this._claimedUsbDevice[cameraKey] = null
      }
      // The write that caused the field bug: it asserts a webcam that is not there, and
      // arriving late it overwrote the deviceType a newer connect had just set.
      if (stillOurs()) setDeviceType(isPrimary ? 'webcam' : undefined)
      else console.log(`[DISCONNECT] ${cameraKey} was re-connected meanwhile — keeping the new camera`)
    } catch (error: unknown) {
      console.error('Disconnect error:', error)
      cogoToast.error('Error during disconnect process')
      cameraLog.uiErrorShown('toast', 'Error during disconnect process', {
        cameraKey,
        error: describeError(error)
      })
    }
  }

  /**
   * Capture from BOTH configured cameras for one countdown (dual-camera booths, e.g.
   * the bahelakotak wardrobe build). Webcam pairs capture in parallel — both frames are
   * synchronous canvas grabs of live streams, so they land within a few ms of the flash.
   * If either side is a tethered DSLR the pair is captured sequentially (PTP/USB is
   * single-outstanding; racing two liveview/shutter transactions manufactures the
   * transient busy/IO errors the per-key plumbing works hard to avoid).
   *
   * A missing/failed side resolves to `undefined` rather than failing the round —
   * dual-camera sessions degrade to the healthy camera instead of stranding guests.
   */
  captureBothImages = async (
    filterFn?: FilterFn,
    frameRatio?: number,
    usePreview: boolean = true,
    onRawCaptured?: () => void,
    /** Per-camera explicit crop rects (kotak custom box); each side crops independently. */
    cropRects?: { primary?: CropRect; secondary?: CropRect }
  ): Promise<{ primary?: File; secondary?: File }> => {
    const store = useCameraStore.getState()
    const bothAreWebcams = store.deviceType === 'webcam' && store.secondaryDeviceType === 'webcam'

    // Signal countdown resume once, when the first raw frame is in hand.
    let rawSignalled = false
    const signalRawOnce = (): void => {
      if (!rawSignalled) {
        rawSignalled = true
        onRawCaptured?.()
      }
    }

    const captureSide = async (key: CameraKey): Promise<File | undefined> => {
      try {
        return await this.captureImage(key, filterFn, frameRatio, usePreview, signalRawOnce, cropRects?.[key])
      } catch (error) {
        console.error(`[captureBothImages] ${key} capture failed:`, error)
        return undefined
      }
    }

    if (bothAreWebcams) {
      const [primary, secondary] = await Promise.all([
        captureSide('primary'),
        captureSide('secondary')
      ])
      return { primary, secondary }
    }

    const primary = await captureSide('primary')
    const secondary = await captureSide('secondary')
    return { primary, secondary }
  }
}

export const unifiedCameraUtils = new UnifiedCameraUtils()

// Notice an unplug when it happens rather than when the next operation fails. No-ops
// outside Chromium/Electron (no navigator.usb), so importing this module stays safe.
installUsbUnplugWatcher({
  // Flag OFF: report nothing claimed, so no disconnect can ever match and the app falls
  // back to discovering an unplug on the next failed operation, exactly as before.
  getClaimed: () =>
    unifiedCameraUtils.isUnplugWatchEnabled()
      ? unifiedCameraUtils.getClaimedUsbDevices()
      : { primary: null, secondary: null },
  // Our own resetUsbDeviceBestEffort() bus-resets the body, which can look like an
  // unplug. Never tear down state under an in-flight connect or recovery.
  isBusyReconnecting: (cameraKey) => unifiedCameraUtils.isReconnecting(cameraKey),
  onUnplugged: (cameraKey) => unifiedCameraUtils.handlePhysicalUnplug(cameraKey)
})
